(function () {
  var ROOT_SELECTOR = '[data-tattoo-preview-studio]';
  var TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  var cutoutLoaderPromise = null;

  if (window.TattooPreviewStudioController) {
    window.TattooPreviewStudioController.initAll(document);
    return;
  }

  var instances = new WeakMap();
  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 47.49em)');
  var CM_PER_INCH = 2.54;
  var PREVIEW_VISUAL_SCALE = 1.5;

  function bool(value) {
    return value === true || value === 'true';
  }

  function isCutoutReady() {
    return window.JPTattooCutout && typeof window.JPTattooCutout.cleanUploadedArtwork === 'function';
  }

  function loadScriptOnce(src) {
    if (!src) return Promise.reject(new Error('Missing cutout asset URL.'));

    window.JPCutoutScriptPromises = window.JPCutoutScriptPromises || {};
    if (window.JPCutoutScriptPromises[src]) return window.JPCutoutScriptPromises[src];

    window.JPCutoutScriptPromises[src] = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.jpCutoutLazy = 'true';
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        reject(new Error('Could not load cutout asset: ' + src));
      };
      document.head.appendChild(script);
    });

    return window.JPCutoutScriptPromises[src];
  }

  function loadCutoutEngine(section) {
    if (isCutoutReady()) return Promise.resolve(true);

    var engineSrc = section && section.dataset ? section.dataset.cutoutEngineSrc : '';
    var wrapperSrc = section && section.dataset ? section.dataset.cutoutWrapperSrc : '';
    if (!engineSrc || !wrapperSrc) return Promise.resolve(false);

    if (!cutoutLoaderPromise) {
      cutoutLoaderPromise = loadScriptOnce(engineSrc)
        .then(function () {
          return loadScriptOnce(wrapperSrc);
        })
        .then(function () {
          return isCutoutReady();
        })
        .catch(function (error) {
          console.warn('Tattoo Preview Studio lazy cutout load failed.', error);
          return false;
        });
    }

    return cutoutLoaderPromise;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function toNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function toCssNumber(value, fallback) {
    var number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function firstNumber() {
    for (var index = 0; index < arguments.length; index += 1) {
      var value = arguments[index];
      if (value === null || value === undefined) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      var number = Number(value);
      if (Number.isFinite(number)) return number;
    }
    return Number.NaN;
  }

  function normalizeBodyPart(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  function normalizeTag(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-');
  }

  function labelFromBodyPart(value) {
    var normalized = normalizeBodyPart(value);
    var labels = {
      wrist: 'Wrist',
      forearm: 'Forearm',
      upper_arm: 'Upper Arm',
      collarbone: 'Collarbone',
      ankle: 'Ankle',
      shoulder: 'Shoulder',
      back: 'Back'
    };
    if (labels[normalized]) return labels[normalized];
    return String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, function (letter) {
        return letter.toUpperCase();
      });
  }

  function safeText(value, fallback) {
    var text = String(value || '').trim();
    return text || fallback || '';
  }

  function withSignal(instance, options) {
    var merged = options || {};
    merged.signal = instance.abort.signal;
    return merged;
  }

  function parseDimensionLabel(label) {
    var text = String(label || '').toLowerCase();
    var match = text.match(/(\d+(?:\.\d+)?)\s*(?:in|inch|")?\s*[x*]\s*(\d+(?:\.\d+)?)/);
    if (!match) return null;
    return {
      width: Number(match[1]),
      height: Number(match[2])
    };
  }

  function formatNumber(value) {
    var rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  function cmToInches(value) {
    return value / CM_PER_INCH;
  }

  class TattooPreviewStudio {
    constructor(section) {
      this.section = section;
      this.abort = new AbortController();
      this.data = { products: [], poses: [] };
      this.products = [];
      this.poses = [];
      this.productsById = new Map();
      this.posesByPart = new Map();
      this.transitionToken = 0;
      this.dragOffset = { x: 0, y: 0 };
      this.activePointers = new Map();
      this.gesture = null;
      this.touchGesture = null;
      this.gestureHudTimer = null;
      this.toolDrag = null;
      this.suppressToolClick = false;
      this.stageFocus = null;
      this.filter = {
        category: '',
        bodyPart: ''
      };
      this.state = {
        activeTab: 'all',
        activeProductGroup: '',
        mobileSearchQuery: '',
        mobileSeriesLabel: '',
        selectedProductId: null,
        selectedVariantId: null,
        activeBodyPart: null,
        activePose: null,
        tattoo: {
          x: 50,
          y: 50,
          scale: 100,
          sizeCm: 5,
          rotation: 0
        },
        isDragging: false,
        isClosed: false,
        dragMode: true,
        lastHapticMark: null
      };
      this.settings = {
        enableDrag: bool(section.dataset.enableDrag),
        enableScale: bool(section.dataset.enableScale),
        enableRotate: bool(section.dataset.enableRotate),
        enableCloseAnimation: bool(section.dataset.enableCloseAnimation),
        enablePoseTransition: bool(section.dataset.enablePoseTransition),
        minSizeCm: toNumber(section.dataset.minSizeCm, 1),
        maxSizeCm: toNumber(section.dataset.maxSizeCm, 10),
        defaultSizeCm: toNumber(section.dataset.defaultSizeCm, 5),
        sizeUnit: section.dataset.sizeUnit || 'cm',
        enableHaptics: bool(section.dataset.enableHaptics),
        enableBasicCutout: bool(section.dataset.enableBasicCutout),
        cutoutSensitivity: toNumber(section.dataset.cutoutSensitivity, 42)
      };
      this.elements = {};
    }

    init() {
      this.cacheElements();
      this.readData();
      this.normalizeSettings();
      this.prepareData();
      this.populateFilterMenus();
      this.bindEvents();
      this.bootInitialState();
      this.syncMobileProductRailMode();
      this.applyFilters();
      this.setInitialAdjustmentState();
      this.updateThemeHeaderOffset();
    }

    cacheElements() {
      var section = this.section;
      this.elements.dataScript = section.querySelector('[data-tattoo-preview-data]');
      this.elements.productCards = Array.from(section.querySelectorAll('[data-product-card]'));
      this.elements.filterEmpty = section.querySelector('[data-filter-empty]');
      this.elements.tabButtons = Array.from(section.querySelectorAll('[data-studio-tab]'));
      this.elements.categoryMenu = section.querySelector('[data-filter-menu="category"]');
      this.elements.bodyMenu = section.querySelector('[data-filter-menu="body"]');
      this.elements.categoryLabel = section.querySelector('[data-category-label]');
      this.elements.bodyLabel = section.querySelector('[data-body-label]');
      this.elements.categoryOptions = section.querySelector('[data-category-options]');
      this.elements.bodyOptions = section.querySelector('[data-body-options]');
      this.elements.mobileProductTrigger = section.querySelector('[data-open-mobile-designs]');
      this.elements.mobileProductLabel = section.querySelector('[data-mobile-product-label]');
      this.elements.mobileDesignsPanel = section.querySelector('[data-mobile-designs-panel]');
      this.elements.closeMobileDesignsButtons = Array.from(section.querySelectorAll('[data-close-mobile-designs]'));
      this.elements.mobileDesignSearch = section.querySelector('[data-mobile-design-search]');
      this.elements.mobileDesignResults = section.querySelector('[data-mobile-design-results]');
      this.elements.mobileSeriesButtons = Array.from(section.querySelectorAll('[data-mobile-series]'));
      this.elements.bodyPicker = section.querySelector('[data-body-picker]');
      this.elements.bodyPartLabel = section.querySelector('[data-body-part-label]');
      this.elements.bodyPartButtons = Array.from(section.querySelectorAll('[data-body-part]'));
      this.elements.modelSourceMobile = section.querySelector('[data-model-source-mobile]');
      this.elements.modelImage = section.querySelector('[data-model-image]');
      this.elements.stage = section.querySelector('[data-preview-stage]');
      this.elements.stageShell = section.querySelector('.tattoo-preview-studio__stage-shell');
      this.elements.closeStageFocusButton = section.querySelector('[data-close-stage-focus]');
      this.elements.stagePlaceholder = section.querySelector('[data-stage-placeholder]');
      this.elements.overlay = section.querySelector('[data-tattoo-overlay]');
      this.elements.overlayImage = section.querySelector('[data-tattoo-overlay-image]');
      this.elements.gestureHud = section.querySelector('[data-gesture-hud]');
      this.elements.gestureSize = section.querySelector('[data-gesture-size]');
      this.elements.gestureRotate = section.querySelector('[data-gesture-rotate]');
      this.elements.allowedRegion = section.querySelector('[data-allowed-region]');
      this.elements.selectedImage = section.querySelector('[data-selected-image]');
      this.elements.selectedTitle = section.querySelector('[data-selected-title]');
      this.elements.selectedDetail = section.querySelector('[data-selected-detail]');
      this.elements.selectedLink = section.querySelector('[data-selected-link]');
      this.elements.addButton = section.querySelector('[data-add-to-cart]');
      this.elements.addLabel = section.querySelector('[data-add-label]');
      this.elements.addPrice = section.querySelector('[data-add-price]');
      this.elements.purchaseConfirm = section.querySelector('[data-purchase-confirm]');
      this.elements.closePurchaseConfirmButton = section.querySelector('[data-close-purchase-confirm]');
      this.elements.confirmAddButton = section.querySelector('[data-confirm-add-to-cart]');
      this.elements.confirmProduct = section.querySelector('[data-confirm-product]');
      this.elements.variantPanel = section.querySelector('[data-variant-panel]');
      this.elements.variantOptions = section.querySelector('[data-variant-options]');
      this.elements.confirmBody = section.querySelector('[data-confirm-body]');
      this.elements.confirmSize = section.querySelector('[data-confirm-size]');
      this.elements.confirmRotate = section.querySelector('[data-confirm-rotate]');
      this.elements.status = section.querySelector('[data-status]');
      this.elements.scaleSlider = section.querySelector('[data-scale-slider]');
      this.elements.scaleOutput = section.querySelector('[data-scale-output]');
      this.elements.sizeLive = section.querySelector('[data-size-live]');
      this.elements.sizeReference = section.querySelector('[data-size-reference]');
      this.elements.rotateSlider = section.querySelector('[data-rotate-slider]');
      this.elements.rotateOutput = section.querySelector('[data-rotate-output]');
      this.elements.placementHint = section.querySelector('[data-placement-hint]');
      this.elements.reopenButton = section.querySelector('[data-reopen-studio]');
      this.elements.resetButton = section.querySelector('[data-reset-tattoo]');
      this.elements.dragButton = section.querySelector('[data-drag-mode]');
      this.elements.openAdjustmentsButtons = Array.from(section.querySelectorAll('[data-open-adjustments]'));
      this.elements.adjustmentDetails = section.querySelector('[data-adjustment-details]');
      this.elements.fitButton = section.querySelector('[data-fit-view]');
      this.elements.focusButton = section.querySelector('[data-focus-view]');
      this.elements.cutoutSheet = section.querySelector('[data-cutout-sheet]');
      this.elements.openCutoutButton = section.querySelector('[data-open-cutout]');
      this.elements.closeCutoutButton = section.querySelector('[data-close-cutout]');
      this.elements.uploadInput = section.querySelector('[data-upload-input]');
      this.elements.triggerUploadButtons = Array.from(section.querySelectorAll('[data-trigger-upload]'));
      this.elements.removeBackgroundButton = section.querySelector('[data-remove-background]');
      this.elements.cutoutSensitivity = section.querySelector('[data-cutout-sensitivity]');
      this.elements.uploadStatus = section.querySelector('[data-upload-status]');
    }

    normalizeSettings() {
      this.settings.minSizeCm = clamp(this.settings.minSizeCm, 1, 100);
      this.settings.maxSizeCm = Math.max(this.settings.minSizeCm, this.settings.maxSizeCm);
      this.settings.defaultSizeCm = clamp(this.settings.defaultSizeCm, this.settings.minSizeCm, this.settings.maxSizeCm);
      this.settings.cutoutSensitivity = clamp(this.settings.cutoutSensitivity, 0, 100);
    }

    updateThemeHeaderOffset() {
      if (!mobileQuery.matches) {
        this.section.style.setProperty('--tps-theme-header-offset', '0px');
        return;
      }

      var selectors = [
        '#shopify-section-header',
        '.shopify-section-header',
        '.section-header',
        'sticky-header',
        'header[role="banner"]',
        '.header-wrapper'
      ];
      var offset = 0;
      var seen = new Set();
      document.querySelectorAll(selectors.join(',')).forEach((element) => {
        if (seen.has(element) || !element.getBoundingClientRect) return;
        seen.add(element);
        var style = window.getComputedStyle(element);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        var rect = element.getBoundingClientRect();
        if (rect.height <= 0 || rect.bottom <= 0 || rect.top > 4) return;
        offset = Math.max(offset, Math.min(rect.bottom, window.innerHeight * 0.34));
      });
      this.section.style.setProperty('--tps-theme-header-offset', Math.ceil(offset) + 'px');
    }

    readData() {
      if (!this.elements.dataScript) return;
      try {
        this.data = JSON.parse(this.elements.dataScript.textContent);
      } catch (error) {
        console.warn('Tattoo Preview Studio data could not be parsed.', error);
      }
    }

    prepareData() {
      this.products = Array.isArray(this.data.products) ? this.data.products : [];
      this.poses = Array.isArray(this.data.poses) ? this.data.poses : [];
      this.bestSellerTag = normalizeTag(this.data.bestSellerTag || 'best-seller');
      this.newInTag = normalizeTag(this.data.newInTag || 'new');

      this.products.forEach((product) => {
        product.id = String(product.id);
        product.variantId = product.variantId ? String(product.variantId) : '';
        product.variants = this.normalizeProductVariants(product);
        var initialVariant = this.getInitialVariant(product);
        product.variantId = initialVariant ? initialVariant.id : product.variantId;
        product.selectedVariantId = product.variantId;
        if (initialVariant) {
          product.price = initialVariant.price || product.price;
          product.priceValue = initialVariant.priceValue ?? product.priceValue;
        }
        product.available = product.rawUploadImage ? false : product.variants.length ? product.variants.some((variant) => variant.available) : Boolean(product.available && product.variantId);
        product.groupId = String(product.groupId || '');
        product.normalizedBodyPart = normalizeBodyPart(product.recommendedBodyPart || product.groupDefaultBodyPart);
        product.groupDefaultBodyPart = normalizeBodyPart(product.groupDefaultBodyPart);
        product.tagsNormalized = Array.isArray(product.tags) ? product.tags.map(normalizeTag) : [];
        product.preparedPreviewImage = product.previewImage || '';
        product.usesCompositePreview = false;

        if (!this.productsById.has(product.id)) {
          this.productsById.set(product.id, product);
        }
      });

      this.poses.forEach((pose) => {
        pose.bodyPart = normalizeBodyPart(pose.bodyPart);
        if (!this.posesByPart.has(pose.bodyPart)) {
          this.posesByPart.set(pose.bodyPart, pose);
        }
      });

      if (!this.elements.tabButtons.some((button) => button.classList.contains('is-active')) && this.elements.tabButtons[0]) {
        this.elements.tabButtons[0].classList.add('is-active');
        this.state.activeTab = this.elements.tabButtons[0].dataset.studioTab || 'all';
        this.state.activeProductGroup = this.elements.tabButtons[0].dataset.productGroup || '';
      }
    }

    bindEvents() {
      this.elements.productCards.forEach((card) => {
        this.on(card, 'click', () => this.selectProduct(card.dataset.productId));
      });

      this.elements.bodyPartButtons.forEach((button) => {
        this.on(button, 'click', () => {
          this.selectBodyPart(button.dataset.bodyPart);
          if (mobileQuery.matches && this.elements.bodyPicker) {
            this.elements.bodyPicker.open = false;
          }
        });
      });

      this.elements.tabButtons.forEach((button) => {
        this.on(button, 'click', () => {
          if (button.dataset.tabUrl) {
            window.location.href = button.dataset.tabUrl;
            return;
          }
          this.state.activeTab = button.dataset.studioTab || 'all';
          this.state.activeProductGroup = button.dataset.productGroup || '';
          this.updateTabButtons();
          this.applyFilters();
          this.ensureVisibleSelection();
        });
      });

      if (this.elements.mobileProductTrigger) {
        this.on(this.elements.mobileProductTrigger, 'click', () => this.openMobileDesignsPanel());
      }

      this.elements.closeMobileDesignsButtons.forEach((button) => {
        this.on(button, 'click', () => this.closeMobileDesignsPanel());
      });

      this.elements.mobileSeriesButtons.forEach((button) => {
        this.on(button, 'click', () => this.selectMobileSeries(button));
      });

      if (this.elements.mobileDesignSearch) {
        this.on(this.elements.mobileDesignSearch, 'input', (event) => this.updateMobileSearch(event.target.value));
      }

      if (this.elements.mobileDesignResults) {
        this.on(this.elements.mobileDesignResults, 'click', (event) => this.selectMobileDesignResult(event));
      }

      if (this.elements.scaleSlider) {
        this.on(this.elements.scaleSlider, 'input', (event) => this.updateScale(event.target.value));
        this.on(this.elements.scaleSlider, 'pointerdown', () => this.section.classList.add('is-sizing'));
        this.on(this.elements.scaleSlider, 'pointerup', () => this.section.classList.remove('is-sizing'));
        this.on(this.elements.scaleSlider, 'pointercancel', () => this.section.classList.remove('is-sizing'));
      }

      if (this.elements.rotateSlider) {
        this.on(this.elements.rotateSlider, 'input', (event) => this.updateRotation(event.target.value));
      }

      if (this.elements.overlay) {
        this.on(this.elements.overlay, 'pointerdown', (event) => this.startDrag(event), { passive: false });
      }

      if (this.elements.stageShell) {
        this.on(this.elements.stageShell, 'pointerdown', (event) => this.startStageGesture(event), { passive: false });
        this.on(this.elements.stageShell, 'click', (event) => this.handleStageShellClick(event));
        this.on(this.elements.stageShell, 'touchstart', (event) => this.startTouchGesture(event), { passive: false });
        this.on(this.elements.stageShell, 'touchmove', (event) => this.handleTouchGesture(event), { passive: false });
        this.on(this.elements.stageShell, 'touchend', (event) => this.endTouchGesture(event), { passive: false });
        this.on(this.elements.stageShell, 'touchcancel', (event) => this.endTouchGesture(event), { passive: false });
      }

      if (this.elements.closeStageFocusButton) {
        this.on(this.elements.closeStageFocusButton, 'click', () => this.closeStageFocus());
      }

      this.on(window, 'pointermove', (event) => this.onDrag(event), { passive: false });
      this.on(window, 'pointermove', (event) => this.onToolDrag(event), { passive: false });
      this.on(window, 'pointerup', (event) => this.endDrag(event));
      this.on(window, 'pointerup', (event) => this.endToolDrag(event));
      this.on(window, 'pointercancel', (event) => this.endDrag(event));
      this.on(window, 'pointercancel', (event) => this.endToolDrag(event));
      this.on(mobileQuery, 'change', () => {
        this.refreshCurrentPoseImage();
        this.setInitialAdjustmentState();
        this.syncMobileProductRailMode();
        this.applyFilters();
        this.updateAddButton();
        this.closeStageFocus();
      });

      this.on(document, 'click', (event) => this.closeFilterMenusOnOutsideClick(event));

      if (this.elements.addButton) {
        this.on(this.elements.addButton, 'click', () => this.openPurchaseConfirm());
      }

      if (this.elements.closePurchaseConfirmButton) {
        this.on(this.elements.closePurchaseConfirmButton, 'click', () => this.closePurchaseConfirm());
      }

      if (this.elements.variantOptions) {
        this.on(this.elements.variantOptions, 'click', (event) => {
          if (!event.target.closest) return;
          var button = event.target.closest('[data-variant-choice]');
          if (!button || !this.elements.variantOptions.contains(button)) return;
          this.selectVariant(button.dataset.variantChoice);
        });
      }

      if (this.elements.confirmAddButton) {
        this.on(this.elements.confirmAddButton, 'click', () => this.addToCart());
      }

      if (this.elements.reopenButton) {
        this.on(this.elements.reopenButton, 'click', () => this.reopenStudio());
      }

      if (this.elements.resetButton) {
        this.on(this.elements.resetButton, 'click', () => this.resetTattoo());
      }

      if (this.elements.dragButton) {
        this.on(this.elements.dragButton, 'click', () => this.toggleDragMode());
      }

      this.on(this.section, 'click', (event) => {
        var adjustmentButton = event.target.closest && event.target.closest('[data-open-adjustments]');
        if (!adjustmentButton || !this.section.contains(adjustmentButton)) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressToolClick = false;
        this.openAdjustments(true);
      }, { capture: true });

      this.elements.openAdjustmentsButtons.forEach((button) => {
        this.on(button, 'pointerdown', (event) => this.startToolDrag(event, button), { passive: false });
        this.on(button, 'click', (event) => {
          if (this.suppressToolClick) {
            event.preventDefault();
            this.suppressToolClick = false;
            return;
          }
          this.openAdjustments(true);
        });
      });

      if (this.elements.adjustmentDetails) {
        this.on(this.elements.adjustmentDetails, 'toggle', () => this.syncAdjustmentButtons());
      }

      if (this.elements.fitButton) {
        this.on(this.elements.fitButton, 'click', () => this.fitToView());
      }

      if (this.elements.focusButton) {
        this.on(this.elements.focusButton, 'click', () => this.toggleFocusMode());
      }

      if (this.elements.openCutoutButton) {
        this.on(this.elements.openCutoutButton, 'click', () => this.openCutoutSheet());
      }

      if (this.elements.closeCutoutButton) {
        this.on(this.elements.closeCutoutButton, 'click', () => this.closeCutoutSheet());
      }

      if (this.elements.cutoutSheet) {
        this.on(this.elements.cutoutSheet, 'click', (event) => {
          if (event.target === this.elements.cutoutSheet) {
            this.closeCutoutSheet();
          }
        });
      }

      this.on(document, 'keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeCutoutSheet();
          this.closePurchaseConfirm();
          this.closeMobileDesignsPanel();
          this.closeAdjustments();
          this.closeStageFocus();
        }
      });

      if (this.elements.uploadInput) {
        this.on(this.elements.uploadInput, 'change', (event) => this.handleUpload(event));
      }

      this.elements.triggerUploadButtons.forEach((button) => {
        this.on(button, 'click', () => {
          this.closeAdjustments();
          this.elements.uploadInput?.click();
        });
      });

      if (this.elements.removeBackgroundButton) {
        this.on(this.elements.removeBackgroundButton, 'click', () => this.recutUploadedImage());
      }

      if (this.elements.cutoutSensitivity) {
        this.on(this.elements.cutoutSensitivity, 'input', (event) => {
          this.settings.cutoutSensitivity = clamp(toNumber(event.target.value, this.settings.cutoutSensitivity), 0, 100);
        });
      }

      this.on(window, 'resize', () => {
        this.updateThemeHeaderOffset();
        this.updateTattooOverlay();
        this.keepToolControlInBounds(Boolean(this.elements.adjustmentDetails && this.elements.adjustmentDetails.open));
      });
      this.on(window, 'orientationchange', () => {
        window.setTimeout(() => {
          this.updateThemeHeaderOffset();
          this.updateTattooOverlay();
          this.keepToolControlInBounds(Boolean(this.elements.adjustmentDetails && this.elements.adjustmentDetails.open));
        }, 180);
      });
    }

    on(target, type, handler, options) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(type, handler, withSignal(this, options));
    }

    bootInitialState() {
      var defaultBodyPart = normalizeBodyPart(this.data.defaultBodyPart);
      var defaultPose = this.posesByPart.get(defaultBodyPart) || this.poses[0] || null;
      this.state.activePose = defaultPose;
      this.state.activeBodyPart = defaultPose ? defaultPose.bodyPart : defaultBodyPart;
      this.updateModelImage(defaultPose);
      this.updateBodyPartButtons();
      this.applyTattooDefaults();
      this.updateAllowedRegion();

      var firstProduct = this.products.find((product) => product.available && product.variantId) || this.products[0] || null;
      if (firstProduct) {
        this.selectProduct(firstProduct.id, { initial: true, respectCurrentPose: true });
      } else {
        this.updateSelectedCard(null);
        this.updateAddButton();
        this.updateTattooOverlay();
      }
    }

    populateFilterMenus() {
      this.populateFilterMenu({
        container: this.elements.categoryOptions,
        label: this.elements.categoryLabel,
        values: this.uniqueValues(this.products.map((product) => product.productType)),
        defaultLabel: 'All Categories',
        onSelect: (value, text) => {
          this.filter.category = value;
          this.elements.categoryLabel.textContent = text;
          this.closeFilterMenus();
          this.applyFilters();
        }
      });

      this.populateFilterMenu({
        container: this.elements.bodyOptions,
        label: this.elements.bodyLabel,
        values: this.uniqueValues(this.products.map((product) => product.normalizedBodyPart).filter(Boolean)),
        defaultLabel: 'All Body Parts',
        labelForValue: (value) => this.poseLabel(value),
        onSelect: (value, text) => {
          this.filter.bodyPart = normalizeBodyPart(value);
          this.elements.bodyLabel.textContent = text;
          this.closeFilterMenus();
          this.applyFilters();
        }
      });
    }

    populateFilterMenu(config) {
      if (!config.container) return;
      config.container.innerHTML = '';
      var allButton = this.createFilterButton('', config.defaultLabel, true);
      allButton.addEventListener('click', () => {
        this.markFilterButton(config.container, allButton);
        config.onSelect('', config.defaultLabel);
      }, { signal: this.abort.signal });
      config.container.append(allButton);

      config.values.forEach((value) => {
        var text = config.labelForValue ? config.labelForValue(value) : value;
        var button = this.createFilterButton(value, text, false);
        button.addEventListener('click', () => {
          this.markFilterButton(config.container, button);
          config.onSelect(value, text);
        }, { signal: this.abort.signal });
        config.container.append(button);
      });

      if (config.label) {
        config.label.textContent = config.defaultLabel;
      }
    }

    createFilterButton(value, text, active) {
      var button = document.createElement('button');
      button.type = 'button';
      button.dataset.value = value;
      button.textContent = text;
      button.classList.toggle('is-active', active);
      return button;
    }

    markFilterButton(container, activeButton) {
      Array.from(container.querySelectorAll('button')).forEach((button) => {
        button.classList.toggle('is-active', button === activeButton);
      });
    }

    closeFilterMenus() {
      this.elements.categoryMenu?.removeAttribute('open');
      this.elements.bodyMenu?.removeAttribute('open');
    }

    closeFilterMenusOnOutsideClick(event) {
      if (this.section.contains(event.target)) {
        if (this.elements.categoryMenu && !this.elements.categoryMenu.contains(event.target)) {
          this.elements.categoryMenu.removeAttribute('open');
        }
        if (this.elements.bodyMenu && !this.elements.bodyMenu.contains(event.target)) {
          this.elements.bodyMenu.removeAttribute('open');
        }
        if (this.elements.bodyPicker && !this.elements.bodyPicker.contains(event.target)) {
          this.elements.bodyPicker.removeAttribute('open');
        }
        if (
          this.elements.adjustmentDetails &&
          this.elements.adjustmentDetails.open &&
          !this.elements.adjustmentDetails.contains(event.target) &&
          !event.target.closest('[data-open-adjustments]')
        ) {
          this.closeAdjustments();
        }
      }
    }

    uniqueValues(values) {
      return Array.from(new Set(values.map((value) => safeText(value, '')).filter(Boolean))).sort();
    }

    shouldUseCompositeProductPreview(product, source) {
      var terms = [
        product && product.title,
        product && product.handle,
        product && product.productType,
        product && product.groupTitle,
        product && product.groupId,
        Array.isArray(product && product.tags) ? product.tags.join(' ') : '',
        source
      ].join(' ').toLowerCase();

      return /pet|photo|portrait|dog|cat/.test(terms);
    }

    async ensurePreviewSource(product) {
      if (!product || product.preparedPreviewImage) return;
      var source = product.previewImage || product.featuredImage || '';
      if (!source) {
        this.setStatus('Add a preview image or upload a design to preview it on the model.');
        return;
      }

      if (product.previewImage || !this.settings.enableBasicCutout) {
        product.preparedPreviewImage = source;
        product.usesCompositePreview = !product.previewImage;
        return;
      }

      this.setStatus('Preparing preview image...');
      try {
        product.preparedPreviewImage = this.shouldUseCompositeProductPreview(product, source)
          ? await this.createCompositeTattooPreview(source)
          : await this.createBasicCutout(source);
        product.usesCompositePreview = false;
        this.setStatus('Temporary tattoo preview prepared. Add custom.preview_image for the cleanest result.');
      } catch (error) {
        console.warn('Tattoo Preview Studio product preview extraction failed.', error);
        try {
          product.preparedPreviewImage = await this.createBasicCutout(source);
          product.usesCompositePreview = false;
          this.setStatus('Temporary preview prepared. Add custom.preview_image for the cleanest result.');
        } catch (fallbackError) {
          console.warn('Tattoo Preview Studio cutout fallback failed.', fallbackError);
          product.preparedPreviewImage = source;
          product.usesCompositePreview = true;
          this.setStatus('Using the tattoo side of the product image as a temporary preview.');
        }
      }
    }

    async handleUpload(event) {
      var file = event.target.files && event.target.files[0];
      if (!file) return;
      if (!file.type || !file.type.startsWith('image/')) {
        this.setUploadStatus('Please upload an image file.');
        return;
      }

      this.setUploadStatus('Loading image...');
      var source = await this.readFileAsDataUrl(file);
      var product = {
        id: 'uploaded-preview',
        groupId: 'upload',
        groupTitle: 'Uploaded Design',
        groupDefaultBodyPart: this.state.activeBodyPart || this.data.defaultBodyPart || '',
        title: file.name.replace(/\.[^.]+$/, '') || 'Uploaded Design',
        productType: 'Upload',
        tags: [],
        tagsNormalized: [],
        featuredImage: source,
        previewImage: '',
        preparedPreviewImage: source,
        usesCompositePreview: false,
        rawUploadImage: source,
        imageAlt: file.name,
        price: '',
        priceValue: 0,
        available: false,
        variantId: '',
        normalizedBodyPart: this.state.activeBodyPart || '',
        defaults: {}
      };

      this.productsById.set(product.id, product);
      this.state.selectedProductId = product.id;
      this.state.selectedVariantId = '';
      this.updateProductCards();
      this.updateSelectedCard(product);
      this.updateAddButton();
      this.applyTattooDefaults();
      this.updateTattooOverlay();
      this.setUploadStatus('Uploaded. Use Smart Cutout for a cleaner preview.');
      this.section.classList.add('has-uploaded-design');
      this.setStatus('Uploaded previews need the Custom Tattoo purchase flow before checkout.');
    }

    async recutUploadedImage() {
      var product = this.getSelectedProduct();
      if (!product || !product.rawUploadImage) {
        this.setUploadStatus('Upload an image first.');
        return;
      }
      if (!this.settings.enableBasicCutout) {
        this.setUploadStatus('Smart cutout is disabled in the Theme Editor.');
        return;
      }

      this.setUploadStatus('Removing background...');
      try {
        product.preparedPreviewImage = await this.createBasicCutout(product.rawUploadImage);
        product.previewImage = product.preparedPreviewImage;
        this.updateSelectedCard(product);
        this.updateTattooOverlay(true);
        this.setUploadStatus('Smart cutout applied.');
      } catch (error) {
        console.warn('Tattoo Preview Studio upload cutout failed.', error);
        this.setUploadStatus(error && error.message ? error.message : 'Could not cut out this image in-browser. Try a transparent PNG.');
      }
    }

    openCutoutSheet() {
      if (!this.elements.cutoutSheet) return;
      this.elements.cutoutSheet.hidden = false;
      this.section.classList.add('is-cutout-open');
      window.setTimeout(() => {
        this.elements.closeCutoutButton?.focus({ preventScroll: true });
      }, 0);
    }

    closeCutoutSheet() {
      if (!this.elements.cutoutSheet || this.elements.cutoutSheet.hidden) return;
      this.elements.cutoutSheet.hidden = true;
      this.section.classList.remove('is-cutout-open');
    }

    setUploadStatus(message) {
      if (this.elements.uploadStatus) {
        this.elements.uploadStatus.textContent = message;
      }
    }

    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        var reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    }

    loadImage(source) {
      return new Promise((resolve, reject) => {
        var image = new Image();
        if (!String(source).startsWith('data:')) {
          image.crossOrigin = 'anonymous';
        }
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = source;
      });
    }

    async createCompositeTattooPreview(source) {
      var image = await this.loadImage(source);
      var sourceWidth = image.naturalWidth || image.width;
      var sourceHeight = image.naturalHeight || image.height;
      if (!sourceWidth || !sourceHeight) {
        throw new Error('Product preview image did not load.');
      }

      var cropX = Math.floor(sourceWidth * 0.58);
      var cropWidth = Math.max(1, sourceWidth - cropX);
      var cropHeight = sourceHeight;
      var canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      var context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(image, cropX, 0, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      var imageData = context.getImageData(0, 0, cropWidth, cropHeight);
      var data = imageData.data;
      var pixelCount = cropWidth * cropHeight;
      var luminanceMap = new Float32Array(pixelCount);
      var alphaMap = new Uint8Array(pixelCount);
      var mask = new Uint8Array(pixelCount);
      var labels = new Int32Array(pixelCount);
      var leftGuard = Math.max(5, Math.round(cropWidth * 0.045));
      var minX = cropWidth;
      var minY = cropHeight;
      var maxX = 0;
      var maxY = 0;
      var inkPixels = 0;

      for (var pixelIndex = 0; pixelIndex < pixelCount; pixelIndex += 1) {
        var baseIndex = pixelIndex * 4;
        luminanceMap[pixelIndex] = data[baseIndex] * 0.299 + data[baseIndex + 1] * 0.587 + data[baseIndex + 2] * 0.114;
      }

      for (var y = 0; y < cropHeight; y += 1) {
        for (var x = 0; x < cropWidth; x += 1) {
          var index = y * cropWidth + x;
          var dataIndex = index * 4;
          var r = data[dataIndex];
          var g = data[dataIndex + 1];
          var b = data[dataIndex + 2];
          var luminance = luminanceMap[index];
          var maxChannel = Math.max(r, g, b);
          var minChannel = Math.min(r, g, b);
          var chroma = maxChannel - minChannel;
          var redBias = r - Math.max(g, b);
          var localTotal = 0;
          var localSamples = 0;

          for (var offsetY = -3; offsetY <= 3; offsetY += 3) {
            var sampleY = y + offsetY;
            if (sampleY < 0 || sampleY >= cropHeight) continue;
            for (var offsetX = -3; offsetX <= 3; offsetX += 3) {
              if (!offsetX && !offsetY) continue;
              var sampleX = x + offsetX;
              if (sampleX < 0 || sampleX >= cropWidth) continue;
              localTotal += luminanceMap[sampleY * cropWidth + sampleX];
              localSamples += 1;
            }
          }

          var localAverage = localSamples ? localTotal / localSamples : luminance;
          var localContrast = localAverage - luminance;
          var edgeSafe = x >= leftGuard && x < cropWidth - 3;
          var contrastInk = luminance < 112 && localContrast > 18 && chroma < 72 && redBias < 42;
          var solidInk = luminance < 72 && maxChannel < 112 && chroma < 78 && redBias < 52;
          var inkCandidate = edgeSafe && (contrastInk || solidInk);
          var contrastStrength = clamp((localContrast - 14) / 44, 0, 1);
          var darkStrength = clamp((118 - luminance) / 76, 0, 1);
          var alpha = inkCandidate ? Math.round(Math.pow(Math.max(contrastStrength, darkStrength), 0.9) * 255) : 0;

          if (alpha < 46) {
            data[dataIndex + 3] = 0;
            continue;
          }

          mask[index] = 1;
          alphaMap[index] = alpha;
        }
      }

      var queue = new Int32Array(pixelCount);
      var components = [];
      var componentId = 0;
      for (var start = 0; start < pixelCount; start += 1) {
        if (!mask[start] || labels[start]) continue;

        componentId += 1;
        var head = 0;
        var tail = 0;
        var area = 0;
        var componentMinX = cropWidth;
        var componentMinY = cropHeight;
        var componentMaxX = 0;
        var componentMaxY = 0;
        queue[tail] = start;
        tail += 1;
        labels[start] = componentId;

        while (head < tail) {
          var current = queue[head];
          head += 1;
          var currentX = current % cropWidth;
          var currentY = Math.floor(current / cropWidth);
          area += 1;
          componentMinX = Math.min(componentMinX, currentX);
          componentMinY = Math.min(componentMinY, currentY);
          componentMaxX = Math.max(componentMaxX, currentX);
          componentMaxY = Math.max(componentMaxY, currentY);

          for (var neighborY = currentY - 1; neighborY <= currentY + 1; neighborY += 1) {
            if (neighborY < 0 || neighborY >= cropHeight) continue;
            for (var neighborX = currentX - 1; neighborX <= currentX + 1; neighborX += 1) {
              if (neighborX < 0 || neighborX >= cropWidth || (neighborX === currentX && neighborY === currentY)) continue;
              var neighbor = neighborY * cropWidth + neighborX;
              if (!mask[neighbor] || labels[neighbor]) continue;
              labels[neighbor] = componentId;
              queue[tail] = neighbor;
              tail += 1;
            }
          }
        }

        var componentWidth = componentMaxX - componentMinX + 1;
        var componentHeight = componentMaxY - componentMinY + 1;
        var edgeBand = Math.max(14, Math.round(cropWidth * 0.075));
        var narrowWidth = Math.max(10, Math.round(cropWidth * 0.06));
        var tallAspect = componentHeight > Math.max(24, componentWidth * 4.2);
        var nearCropEdge = componentMaxX >= cropWidth - edgeBand || componentMinX <= leftGuard + edgeBand;
        var thinLine = componentWidth <= narrowWidth && tallAspect && nearCropEdge;
        var rightEdgeLine = componentMaxX >= cropWidth - edgeBand && componentWidth <= Math.max(16, Math.round(cropWidth * 0.09));
        if (area >= 8 && !thinLine && !rightEdgeLine) {
          components.push({
            id: componentId,
            area: area,
            minX: componentMinX,
            minY: componentMinY,
            maxX: componentMaxX,
            maxY: componentMaxY,
            width: componentWidth,
            height: componentHeight
          });
        }
      }

      components.sort(function(a, b) {
        return b.area - a.area;
      });

      if (!components.length) {
        throw new Error('Could not identify tattoo ink in the product image.');
      }

      var primary = components[0];
      var bboxPad = Math.round(Math.max(primary.width, primary.height) * 0.24);
      var keepLabels = new Uint8Array(componentId + 1);
      components.forEach(function(component) {
        var centerX = (component.minX + component.maxX) / 2;
        var centerY = (component.minY + component.maxY) / 2;
        var nearPrimary = centerX >= primary.minX - bboxPad
          && centerX <= primary.maxX + bboxPad
          && centerY >= primary.minY - bboxPad
          && centerY <= primary.maxY + bboxPad;
        if (
          component.id === primary.id
          || (nearPrimary && component.area >= Math.max(10, primary.area * 0.003))
        ) {
          keepLabels[component.id] = 1;
        }
      });

      var keepMask = new Uint8Array(pixelCount);
      var preliminaryMinX = cropWidth;
      var preliminaryMaxX = 0;
      for (var maskIndex = 0; maskIndex < pixelCount; maskIndex += 1) {
        var maskLabel = labels[maskIndex];
        if (!maskLabel || !keepLabels[maskLabel]) continue;
        keepMask[maskIndex] = 1;
        var maskX = maskIndex % cropWidth;
        preliminaryMinX = Math.min(preliminaryMinX, maskX);
        preliminaryMaxX = Math.max(preliminaryMaxX, maskX);
      }

      var edgeScrubWidth = Math.max(14, Math.round(cropWidth * 0.12));
      var scrubColumn = function(columnX) {
        var count = 0;
        var longestRun = 0;
        var currentRun = 0;
        for (var columnY = 0; columnY < cropHeight; columnY += 1) {
          var columnIndex = columnY * cropWidth + columnX;
          if (keepMask[columnIndex]) {
            count += 1;
            currentRun += 1;
            longestRun = Math.max(longestRun, currentRun);
          } else {
            currentRun = 0;
          }
        }

        var isLineColumn =
          count > Math.max(18, cropHeight * 0.055) &&
          longestRun > Math.max(18, cropHeight * 0.045) &&
          count / Math.max(1, longestRun) > 0.46;
        if (!isLineColumn) return;

        for (var clearX = Math.max(0, columnX - 1); clearX <= Math.min(cropWidth - 1, columnX + 1); clearX += 1) {
          for (var clearY = 0; clearY < cropHeight; clearY += 1) {
            keepMask[clearY * cropWidth + clearX] = 0;
          }
        }
      };

      if (preliminaryMinX <= preliminaryMaxX) {
        var hardInset = Math.max(4, Math.round(cropWidth * 0.018));
        for (var insetLeftX = preliminaryMinX; insetLeftX < Math.min(preliminaryMaxX, preliminaryMinX + hardInset); insetLeftX += 1) {
          for (var insetLeftY = 0; insetLeftY < cropHeight; insetLeftY += 1) {
            keepMask[insetLeftY * cropWidth + insetLeftX] = 0;
          }
        }
        for (var insetRightX = Math.max(preliminaryMinX, preliminaryMaxX - hardInset + 1); insetRightX <= preliminaryMaxX; insetRightX += 1) {
          for (var insetRightY = 0; insetRightY < cropHeight; insetRightY += 1) {
            keepMask[insetRightY * cropWidth + insetRightX] = 0;
          }
        }
        for (var leftX = preliminaryMinX; leftX <= Math.min(preliminaryMaxX, preliminaryMinX + edgeScrubWidth); leftX += 1) {
          scrubColumn(leftX);
        }
        for (var rightX = Math.max(preliminaryMinX, preliminaryMaxX - edgeScrubWidth); rightX <= preliminaryMaxX; rightX += 1) {
          scrubColumn(rightX);
        }

      }

      for (var outputIndex = 0; outputIndex < pixelCount; outputIndex += 1) {
        var outputDataIndex = outputIndex * 4;
        if (!keepMask[outputIndex]) {
          data[outputDataIndex + 3] = 0;
          continue;
        }

        var outputX = outputIndex % cropWidth;
        var outputY = Math.floor(outputIndex / cropWidth);
        data[outputDataIndex] = 8;
        data[outputDataIndex + 1] = 18;
        data[outputDataIndex + 2] = 32;
        data[outputDataIndex + 3] = alphaMap[outputIndex];
        minX = Math.min(minX, outputX);
        minY = Math.min(minY, outputY);
        maxX = Math.max(maxX, outputX);
        maxY = Math.max(maxY, outputY);
        inkPixels += 1;
      }

      if (inkPixels < Math.max(24, cropWidth * cropHeight * 0.0005)) {
        throw new Error('Could not isolate tattoo ink in the product image.');
      }

      context.putImageData(imageData, 0, 0);

      var trimWidth = maxX - minX + 1;
      var leftArtifactTrimX = Math.max(3, Math.round(trimWidth * 0.04));
      var rightArtifactTrimX = Math.max(12, Math.round(trimWidth * 0.24));
      if (trimWidth > (leftArtifactTrimX + rightArtifactTrimX) * 2) {
        minX += leftArtifactTrimX;
        maxX -= rightArtifactTrimX;
      }

      var padding = Math.max(4, Math.round(Math.min(cropWidth, cropHeight) * 0.018));
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(cropWidth - 1, maxX + padding);
      maxY = Math.min(cropHeight - 1, maxY + padding);

      var trimmedWidth = Math.max(1, maxX - minX + 1);
      var trimmedHeight = Math.max(1, maxY - minY + 1);
      var outputRatio = Math.max(trimmedWidth / trimmedHeight, trimmedHeight / trimmedWidth);
      if (outputRatio > 2.15) {
        throw new Error('Extracted tattoo preview is too narrow for a clean product preview.');
      }

      var trimmed = document.createElement('canvas');
      trimmed.width = trimmedWidth;
      trimmed.height = trimmedHeight;
      trimmed.getContext('2d').drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);
      return trimmed.toDataURL('image/png');
    }

    createSingleInkCanvas(sourceCanvas) {
      var canvas = document.createElement('canvas');
      canvas.width = sourceCanvas.width;
      canvas.height = sourceCanvas.height;
      var context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(sourceCanvas, 0, 0);

      var imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      var data = imageData.data;
      var ink = { r: 8, g: 18, b: 32 };

      for (var index = 0; index < data.length; index += 4) {
        var alpha = data[index + 3];
        if (alpha <= 8) {
          data[index + 3] = 0;
          continue;
        }

        var r = data[index];
        var g = data[index + 1];
        var b = data[index + 2];
        var luminance = r * 0.299 + g * 0.587 + b * 0.114;
        var lightPixel = luminance > 168 || (r > 150 && g > 150 && b > 150);

        if (lightPixel) {
          data[index + 3] = 0;
          continue;
        }

        var inkStrength = clamp((184 - luminance) / 124, 0.34, 1);
        var newAlpha = Math.round(alpha * inkStrength);
        if (newAlpha < 18) {
          data[index + 3] = 0;
          continue;
        }

        data[index] = ink.r;
        data[index + 1] = ink.g;
        data[index + 2] = ink.b;
        data[index + 3] = newAlpha;
      }

      context.putImageData(imageData, 0, 0);
      return canvas;
    }

    async createBasicCutout(source) {
      var image = await this.loadImage(source);
      var sensitivity = this.elements.cutoutSensitivity
        ? toNumber(this.elements.cutoutSensitivity.value, this.settings.cutoutSensitivity)
        : this.settings.cutoutSensitivity;

      await loadCutoutEngine(this.section);

      if (isCutoutReady()) {
        var result = await window.JPTattooCutout.cleanUploadedArtwork(image, {
          backgroundSensitivity: sensitivity,
          maxSide: 1200,
          preset: 'auto',
          protectInk: true
        });

        if (result && result.status === 'success' && result.canvas && result.canvas.toDataURL) {
          return this.createSingleInkCanvas(result.canvas).toDataURL('image/png');
        }

        var message = result && result.message
          ? result.message
          : 'This image needs a cleaner upload or transparent PNG.';
        var error = new Error(message);
        error.cutoutStatus = result && result.status;
        throw error;
      }

      var maxSide = 1200;
      var scale = Math.min(1, maxSide / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
      var width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
      var height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
      var canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      var context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(image, 0, 0, width, height);
      var imageData = context.getImageData(0, 0, width, height);
      var data = imageData.data;
      var corner = this.sampleCornerColor(data, width, height);
      var threshold = 18 + sensitivity * 1.45;
      var feather = Math.max(10, threshold * 0.34);

      for (var index = 0; index < data.length; index += 4) {
        var distance = Math.sqrt(
          Math.pow(data[index] - corner.r, 2) +
          Math.pow(data[index + 1] - corner.g, 2) +
          Math.pow(data[index + 2] - corner.b, 2)
        );
        if (distance < threshold) {
          data[index + 3] = 0;
        } else if (distance < threshold + feather) {
          data[index + 3] = Math.round(data[index + 3] * ((distance - threshold) / feather));
        }
      }

      context.putImageData(imageData, 0, 0);
      return this.createSingleInkCanvas(canvas).toDataURL('image/png');
    }

    sampleCornerColor(data, width, height) {
      var points = [
        [0, 0],
        [width - 1, 0],
        [0, height - 1],
        [width - 1, height - 1]
      ];
      var total = { r: 0, g: 0, b: 0 };
      points.forEach((point) => {
        var index = (point[1] * width + point[0]) * 4;
        total.r += data[index];
        total.g += data[index + 1];
        total.b += data[index + 2];
      });
      return {
        r: total.r / points.length,
        g: total.g / points.length,
        b: total.b / points.length
      };
    }

    async selectProduct(productId, options) {
      var product = this.productsById.get(String(productId));
      if (!product) return;

      this.state.selectedProductId = product.id;
      this.state.selectedVariantId = product.selectedVariantId || product.variantId;
      this.updateProductCards();
      this.updateSelectedCard(product);
      this.updateAddButton();
      await this.ensurePreviewSource(product);

      var requestedBodyPart = product.normalizedBodyPart || product.groupDefaultBodyPart;
      var shouldSwitchPose =
        !options?.respectCurrentPose &&
        requestedBodyPart &&
        this.posesByPart.has(requestedBodyPart) &&
        requestedBodyPart !== this.state.activeBodyPart;

      if (shouldSwitchPose) {
        await this.selectBodyPart(requestedBodyPart, { keepProduct: true });
      } else {
        this.applyTattooDefaults();
        this.updateTattooOverlay();
        this.updateHint();
      }
    }

    async selectBodyPart(bodyPart, options) {
      var normalized = normalizeBodyPart(bodyPart);
      var pose = this.posesByPart.get(normalized);
      if (!pose) {
        this.setStatus('Add a ' + labelFromBodyPart(normalized) + ' body pose block in the Theme Editor.');
        return false;
      }

      if (this.state.activeBodyPart === pose.bodyPart && this.state.activePose === pose) {
        if (!options?.keepProduct) {
          this.resetTattoo();
        }
        this.updateBodyPartButtons();
        this.updateHint();
        return true;
      }

      var changed = await this.transitionToPose(pose);
      if (changed) {
        this.state.activeBodyPart = pose.bodyPart;
        this.applyTattooDefaults();
        this.updateTattooOverlay();
        this.updateBodyPartButtons();
        this.updateHint();
      }
      return changed;
    }

    async transitionToPose(pose) {
      var imageSource = this.getPoseImage(pose);
      var token = this.transitionToken + 1;
      this.transitionToken = token;

      if (!imageSource) {
        this.state.activePose = pose;
        this.updateModelImage(pose);
        return true;
      }

      if (!this.settings.enablePoseTransition || reduceMotionQuery.matches || !this.elements.modelImage) {
        try {
          await this.preloadImage(imageSource);
          if (token !== this.transitionToken) return false;
          this.state.activePose = pose;
          this.updateModelImage(pose);
          return true;
        } catch (error) {
          console.warn('Tattoo Preview Studio pose image failed to load.', imageSource, error);
          return false;
        }
      }

      this.elements.overlay?.classList.add('is-updating');
      this.elements.stageShell?.classList.add('is-pose-transitioning');
      this.elements.modelImage.classList.add('is-leaving');

      try {
        await this.preloadImage(imageSource);
        await this.wait(150);
        if (token !== this.transitionToken) return false;
        this.state.activePose = pose;
        this.updateModelImage(pose);
        this.elements.modelImage.classList.remove('is-active', 'is-leaving');
        this.elements.modelImage.classList.add('is-entering');
        await this.nextFrame();
        this.elements.modelImage.classList.remove('is-entering');
        this.elements.modelImage.classList.add('is-active');
        await this.waitForTransition(this.elements.modelImage, 520);
        return true;
      } catch (error) {
        console.warn('Tattoo Preview Studio pose image failed to load.', imageSource, error);
        this.elements.modelImage.classList.remove('is-leaving', 'is-entering');
        this.elements.modelImage.classList.add('is-active');
        return false;
      } finally {
        if (token === this.transitionToken) {
          this.elements.overlay?.classList.remove('is-updating');
          this.elements.stageShell?.classList.remove('is-pose-transitioning');
        }
      }
    }

    updateModelImage(pose) {
      var sources = this.getPoseSources(pose);
      var desktopImage = sources.desktop;
      var mobileImage = sources.mobile;
      var hasImage = Boolean(desktopImage || mobileImage);
      var image = this.elements.modelImage;

      if (this.elements.modelSourceMobile) {
        if (mobileImage) {
          this.elements.modelSourceMobile.setAttribute('srcset', mobileImage);
        } else {
          this.elements.modelSourceMobile.removeAttribute('srcset');
        }
      }

      if (image) {
        image.src = desktopImage || mobileImage || TRANSPARENT_PIXEL;
        image.alt = pose ? pose.label || labelFromBodyPart(pose.bodyPart) : '';
        image.hidden = !hasImage;
        image.classList.remove('is-leaving', 'is-entering');
        image.classList.add('is-active');
      }

      if (this.elements.stagePlaceholder) {
        this.elements.stagePlaceholder.hidden = hasImage;
      }

      if (this.elements.stageShell) {
        this.elements.stageShell.classList.toggle('has-model', hasImage);
        this.elements.stageShell.classList.toggle('has-no-model', !hasImage);
      }
      this.section.classList.toggle('tattoo-preview-studio--has-model', hasImage);
      this.section.classList.toggle('tattoo-preview-studio--has-no-model', !hasImage);

      this.updateAllowedRegion();
    }

    refreshCurrentPoseImage() {
      if (!this.state.activePose) return;
      this.updateModelImage(this.state.activePose);
    }

    getPoseSources(pose) {
      if (!pose) {
        return { desktop: '', mobile: '' };
      }
      return {
        desktop: pose.desktopImage || pose.mobileImage || '',
        mobile: pose.mobileImage || pose.desktopImage || ''
      };
    }

    getPoseImage(pose) {
      var sources = this.getPoseSources(pose);
      if (mobileQuery.matches) {
        return sources.mobile || sources.desktop || '';
      }
      return sources.desktop || sources.mobile || '';
    }

    getSelectedProduct() {
      return this.productsById.get(String(this.state.selectedProductId || '')) || null;
    }

    normalizeProductVariants(product) {
      var variants = Array.isArray(product.variants) ? product.variants : [];
      var normalized = variants
        .map((variant) => {
          return {
            id: variant && variant.id ? String(variant.id) : '',
            title: safeText(variant && variant.title, ''),
            options: Array.isArray(variant && variant.options) ? variant.options.filter(Boolean).map(String) : [],
            available: Boolean(variant && variant.available),
            price: safeText(variant && variant.price, product.price || ''),
            priceValue: variant && variant.priceValue != null ? variant.priceValue : product.priceValue
          };
        })
        .filter((variant) => variant.id);

      if (!normalized.length && product.variantId) {
        normalized.push({
          id: String(product.variantId),
          title: product.sizeLabel || 'Standard',
          options: product.sizeLabel ? [product.sizeLabel] : [],
          available: Boolean(product.available),
          price: product.price || '',
          priceValue: product.priceValue
        });
      }

      return normalized;
    }

    getInitialVariant(product) {
      if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
      var preferredId = String(product.variantId || product.selectedVariantId || '');
      return (
        product.variants.find((variant) => variant.id === preferredId && variant.available) ||
        product.variants.find((variant) => variant.available) ||
        product.variants.find((variant) => variant.id === preferredId) ||
        product.variants[0]
      );
    }

    getSelectedVariant(product) {
      if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
      var selectedId = String(this.state.selectedVariantId || product.selectedVariantId || product.variantId || '');
      return (
        product.variants.find((variant) => variant.id === selectedId) ||
        product.variants.find((variant) => variant.id === String(product.variantId || '')) ||
        product.variants.find((variant) => variant.available) ||
        product.variants[0]
      );
    }

    formatVariantTitle(variant, product) {
      if (!variant) return product && product.sizeLabel ? product.sizeLabel : 'Standard';
      var title = safeText(variant.title, '');
      var optionText = Array.isArray(variant.options)
        ? variant.options
            .map((option) => safeText(option, ''))
            .filter((option) => option && option.toLowerCase() !== 'default title')
            .join(' / ')
        : '';

      if (!title || title.toLowerCase() === 'default title') {
        return safeText(optionText, product && product.sizeLabel ? product.sizeLabel : 'Standard');
      }

      return title;
    }

    selectVariant(variantId) {
      var product = this.getSelectedProduct();
      if (!product || !Array.isArray(product.variants)) return;
      var variant = product.variants.find((item) => item.id === String(variantId));
      if (!variant || !variant.available) return;

      this.state.selectedVariantId = variant.id;
      product.selectedVariantId = variant.id;
      product.variantId = variant.id;
      product.price = variant.price || product.price;
      product.priceValue = variant.priceValue ?? product.priceValue;
      this.renderVariantOptions(product);
      this.updateSelectedCard(product);
      this.updateAddButton();
      if (this.elements.purchaseConfirm && !this.elements.purchaseConfirm.hidden) {
        this.updatePurchaseConfirm();
      }
    }

    renderVariantOptions(product) {
      if (!this.elements.variantPanel || !this.elements.variantOptions) return;
      this.elements.variantOptions.innerHTML = '';

      if (!product || !Array.isArray(product.variants) || !product.variants.length) {
        this.elements.variantPanel.hidden = true;
        return;
      }

      this.elements.variantPanel.hidden = false;
      var selectedVariant = this.getSelectedVariant(product);

      product.variants.forEach((variant) => {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'tattoo-preview-studio__variant-choice';
        button.dataset.variantChoice = variant.id;
        button.disabled = !variant.available;
        button.setAttribute('aria-pressed', selectedVariant && selectedVariant.id === variant.id ? 'true' : 'false');

        var label = document.createElement('span');
        label.textContent = this.formatVariantTitle(variant, product);
        var price = document.createElement('small');
        price.textContent = variant.available ? variant.price || '' : 'Unavailable';

        button.append(label, price);
        this.elements.variantOptions.append(button);
      });
    }

    hasProductPlacementDefaults(productDefaults) {
      if (!productDefaults) return false;
      return [productDefaults.x, productDefaults.y, productDefaults.scale, productDefaults.rotation].some(function(value) {
        if (value === null || value === undefined) return false;
        return !(typeof value === 'string' && value.trim() === '');
      });
    }

    getVisualPoseFallback(pose, productDefaults, poseDefaults) {
      if (!pose || normalizeBodyPart(pose.bodyPart) !== 'upper_arm') return null;
      if (this.hasProductPlacementDefaults(productDefaults)) return null;

      var poseX = firstNumber(poseDefaults.x, 50);
      var poseY = firstNumber(poseDefaults.y, 50);
      var poseScale = firstNumber(poseDefaults.scale, 100);
      var isPlaceholderPose =
        Math.abs(poseX - 50) <= 0.01 &&
        Math.abs(poseY - 50) <= 0.01 &&
        Math.abs(poseScale - 100) <= 0.01;

      if (!isPlaceholderPose) return null;

      return {
        x: 73,
        y: 66,
        scale: 45,
        rotation: 0
      };
    }

    applyTattooDefaults() {
      var pose = this.state.activePose;
      var product = this.getSelectedProduct();
      var poseDefaults = pose && pose.defaultTattoo ? pose.defaultTattoo : {};
      var productDefaults = product && product.defaults ? product.defaults : {};
      var visualFallback = this.getVisualPoseFallback(pose, productDefaults, poseDefaults) || {};
      var defaultScale = firstNumber(productDefaults.scale, visualFallback.scale, poseDefaults.scale, NaN);
      var visualFallbackScale = firstNumber(visualFallback.scale, NaN);
      var defaultSize = Number.isFinite(visualFallbackScale)
        ? this.scaleToSizeCm(visualFallbackScale)
        : this.getComfortableDefaultSizeCm(defaultScale);

      this.state.tattoo = {
        x: clamp(firstNumber(productDefaults.x, visualFallback.x, poseDefaults.x, 50), 0, 100),
        y: clamp(firstNumber(productDefaults.y, visualFallback.y, poseDefaults.y, 50), 0, 100),
        scale: this.sizeCmToScale(defaultSize),
        sizeCm: defaultSize,
        rotation: clamp(firstNumber(productDefaults.rotation, visualFallback.rotation, poseDefaults.rotation, 0), -180, 180)
      };

      this.clampTattooToRegion();
      this.updateAdjustmentControls();
      this.updateAllowedRegion();
    }

    resetTattoo() {
      this.applyTattooDefaults();
      this.updateTattooOverlay(true);
      this.setStatus('Preview reset.');
    }

    updateScale(value) {
      if (!this.settings.enableScale) return;
      this.state.tattoo.sizeCm = clamp(toNumber(value, this.state.tattoo.sizeCm), this.settings.minSizeCm, this.settings.maxSizeCm);
      this.state.tattoo.scale = this.sizeCmToScale(this.state.tattoo.sizeCm);
      this.updateAdjustmentControls();
      this.updateTattooOverlay();
      this.showGestureHud();
      this.maybeVibrateForSize();
    }

    updateRotation(value) {
      if (!this.settings.enableRotate) return;
      this.state.tattoo.rotation = clamp(toNumber(value, this.state.tattoo.rotation), -180, 180);
      this.updateAdjustmentControls();
      this.updateTattooOverlay();
      this.showGestureHud();
    }

    updateAdjustmentControls() {
      if (this.elements.scaleSlider) {
        this.elements.scaleSlider.min = this.settings.minSizeCm;
        this.elements.scaleSlider.max = this.settings.maxSizeCm;
        this.elements.scaleSlider.step = '0.1';
        this.elements.scaleSlider.value = formatNumber(this.state.tattoo.sizeCm);
      }
      var sizeText = this.formatCurrentSize();
      if (this.elements.scaleOutput) {
        this.elements.scaleOutput.textContent = sizeText.primary;
      }
      if (this.elements.sizeLive) {
        this.elements.sizeLive.textContent = mobileQuery.matches ? sizeText.primary.toUpperCase() : sizeText.full;
      }
      if (this.elements.rotateSlider) {
        this.elements.rotateSlider.value = Math.round(this.state.tattoo.rotation);
      }
      if (this.elements.rotateOutput) {
        this.elements.rotateOutput.textContent = Math.round(this.state.tattoo.rotation) + ' deg';
      }
      this.updateSizeReference();
      if (this.elements.purchaseConfirm && !this.elements.purchaseConfirm.hidden) {
        this.updatePurchaseConfirm();
      }
    }

    updateSizeReference() {
      if (!this.elements.sizeReference) return;
      var product = this.getSelectedProduct();
      if (!product) {
        this.elements.sizeReference.textContent = 'Preview size: ' + this.formatCurrentSize().full;
        return;
      }

      var selectedVariant = this.getSelectedVariant(product);
      var orderSize = this.formatVariantTitle(selectedVariant, product);
      var widthIn = toNumber(product.previewWidthIn, NaN);
      var heightIn = toNumber(product.previewHeightIn, NaN);
      if (!Number.isFinite(widthIn) || !Number.isFinite(heightIn) || widthIn <= 0 || heightIn <= 0) {
        var parsed = parseDimensionLabel(product.sizeLabel);
        if (parsed) {
          widthIn = parsed.width;
          heightIn = parsed.height;
        }
      }

      if (Number.isFinite(widthIn) && Number.isFinite(heightIn) && widthIn > 0 && heightIn > 0) {
        this.elements.sizeReference.textContent = 'Preview footprint: ' + this.formatSizeWithAspect(widthIn, heightIn);
      } else if (selectedVariant) {
        this.elements.sizeReference.textContent = 'Print size: ' + orderSize + ' / preview ' + this.formatCurrentSize().full;
      } else {
        this.elements.sizeReference.textContent = 'Preview size: ' + this.formatCurrentSize().full;
      }
    }

    formatInches(value) {
      return String(Math.round(value * 10) / 10);
    }

    sizeCmToScale(sizeCm) {
      return (clamp(sizeCm, this.settings.minSizeCm, this.settings.maxSizeCm) / this.settings.defaultSizeCm) * 100;
    }

    scaleToSizeCm(scale) {
      return clamp(this.settings.defaultSizeCm * (scale / 100), this.settings.minSizeCm, this.settings.maxSizeCm);
    }

    getComfortableDefaultSizeCm(scale) {
      var fromScale = Number.isFinite(scale) && scale >= 60 ? this.scaleToSizeCm(scale) : this.settings.defaultSizeCm;
      var comfortable = Math.max(fromScale, this.settings.defaultSizeCm, 6.5);
      return clamp(comfortable, this.settings.minSizeCm, this.settings.maxSizeCm);
    }

    getPreviewPixelsPerCm() {
      if (!this.elements.stageShell) return 12;
      var rect = this.elements.stageShell.getBoundingClientRect();
      if (!rect.width || !rect.height) return 12;
      var fromWidth = rect.width / 34;
      var fromHeight = rect.height / 46;
      return clamp(Math.min(fromWidth, fromHeight), 8, 24);
    }

    getTattooRenderWidthPx() {
      if (!this.elements.stageShell) return 84;
      var rect = this.elements.stageShell.getBoundingClientRect();
      var width = this.state.tattoo.sizeCm * this.getPreviewPixelsPerCm() * PREVIEW_VISUAL_SCALE;
      var minWidth = mobileQuery.matches ? 78 : 96;
      var maxWidth = rect.width ? rect.width * (mobileQuery.matches ? 0.54 : 0.48) : 420;
      return Math.round(clamp(width, minWidth, Math.max(minWidth, maxWidth)));
    }

    formatCurrentSize() {
      var cm = this.state.tattoo.sizeCm || this.settings.defaultSizeCm;
      var inch = cmToInches(cm);
      var cmText = formatNumber(cm) + ' cm';
      var inText = formatNumber(inch) + ' in';
      return {
        primary: this.settings.sizeUnit === 'in' ? inText : cmText,
        secondary: this.settings.sizeUnit === 'in' ? cmText : inText,
        full: this.settings.sizeUnit === 'in' ? inText + ' / ' + cmText : cmText + ' / ' + inText
      };
    }

    formatSizeWithAspect(widthIn, heightIn) {
      var longEdgeIn = Math.max(widthIn, heightIn);
      if (!longEdgeIn || !Number.isFinite(longEdgeIn)) return this.formatCurrentSize().full;
      var scale = cmToInches(this.state.tattoo.sizeCm) / longEdgeIn;
      var finalWidthIn = widthIn * scale;
      var finalHeightIn = heightIn * scale;
      var finalWidthCm = finalWidthIn * CM_PER_INCH;
      var finalHeightCm = finalHeightIn * CM_PER_INCH;
      var cmText = formatNumber(finalWidthCm) + ' x ' + formatNumber(finalHeightCm) + ' cm';
      var inText = formatNumber(finalWidthIn) + ' x ' + formatNumber(finalHeightIn) + ' in';
      return this.settings.sizeUnit === 'in' ? inText + ' / ' + cmText : cmText + ' / ' + inText;
    }

    maybeVibrateForSize() {
      if (!this.settings.enableHaptics || !mobileQuery.matches || !navigator.vibrate) return;
      var mark = Math.round(this.state.tattoo.sizeCm);
      var atEdge = this.state.tattoo.sizeCm <= this.settings.minSizeCm || this.state.tattoo.sizeCm >= this.settings.maxSizeCm;
      var shouldVibrate = atEdge || Math.abs(this.state.tattoo.sizeCm - mark) < 0.04;
      if (shouldVibrate && this.state.lastHapticMark !== mark + ':' + atEdge) {
        this.state.lastHapticMark = mark + ':' + atEdge;
        navigator.vibrate(8);
      }
    }

    showGestureHud(duration) {
      if (!this.elements.gestureHud || !mobileQuery.matches) return;
      this.updateGestureHud();
      this.elements.gestureHud.hidden = false;
      window.clearTimeout(this.gestureHudTimer);
      this.gestureHudTimer = window.setTimeout(() => {
        this.hideGestureHud();
      }, duration || 900);
    }

    hideGestureHud() {
      if (this.elements.gestureHud) {
        this.elements.gestureHud.hidden = true;
      }
    }

    updateGestureHud() {
      if (this.elements.gestureSize) {
        this.elements.gestureSize.textContent = this.formatCurrentSize().primary;
      }
      if (this.elements.gestureRotate) {
        this.elements.gestureRotate.textContent = Math.round(this.state.tattoo.rotation) + ' deg';
      }
    }

    updateTattooOverlay(isReset) {
      var product = this.getSelectedProduct();
      var overlay = this.elements.overlay;
      var image = this.elements.overlayImage;
      var source = product ? product.preparedPreviewImage || product.previewImage || product.featuredImage : '';
      var hasPoseImage = Boolean(this.getPoseImage(this.state.activePose));

      if (!overlay || !image || !product || !source || !this.state.activePose || !hasPoseImage) {
        if (overlay) overlay.hidden = true;
        return;
      }

      overlay.hidden = false;
      overlay.style.left = this.state.tattoo.x + '%';
      overlay.style.top = this.state.tattoo.y + '%';
      overlay.style.width = this.getTattooRenderWidthPx() + 'px';
      overlay.style.setProperty('--tattoo-scale', '1');
      overlay.style.setProperty('--tattoo-rotation', this.state.tattoo.rotation + 'deg');

      if (image.dataset.src !== source) {
        overlay.classList.add('is-updating');
        image.onload = () => overlay.classList.remove('is-updating');
        image.onerror = () => overlay.classList.remove('is-updating');
        image.src = source;
        image.alt = product.title || '';
        image.dataset.src = source;
      }
      overlay.classList.toggle('is-composite-preview', Boolean(product.usesCompositePreview && !product.rawUploadImage));

      if (isReset && !reduceMotionQuery.matches && overlay.animate) {
        overlay.animate([{ opacity: 0.72 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
    }

    startDrag(event) {
      var canGesture = this.settings.enableScale || this.settings.enableRotate;
      if ((!this.settings.enableDrag && !canGesture) || !this.elements.stageShell) return;
      var overlay = this.elements.overlay;
      if (!overlay || overlay.hidden) return;

      event.preventDefault();
      var pointerId = event.pointerId != null ? event.pointerId : 'mouse';
      this.activePointers.set(pointerId, { x: event.clientX, y: event.clientY });

      if (this.activePointers.size >= 2 && canGesture) {
        this.initGesture();
      } else if (this.settings.enableDrag && this.state.dragMode) {
        this.state.isDragging = true;
        overlay.classList.add('is-dragging');
        this.elements.stageShell.classList.add('is-drag-mode');

        var rect = this.elements.stageShell.getBoundingClientRect();
        var currentX = rect.left + (this.state.tattoo.x / 100) * rect.width;
        var currentY = rect.top + (this.state.tattoo.y / 100) * rect.height;
        this.dragOffset.x = currentX - event.clientX;
        this.dragOffset.y = currentY - event.clientY;
      }

      if (overlay.setPointerCapture && event.pointerId != null) {
        overlay.setPointerCapture(event.pointerId);
      }
    }

    startStageGesture(event) {
      var canGesture = this.settings.enableScale || this.settings.enableRotate;
      if (this.touchGesture && event.pointerType !== 'mouse') return;
      if (!canGesture || !this.elements.stageShell) return;
      if (!this.elements.overlay || this.elements.overlay.hidden) return;
      if (event.pointerType === 'mouse') return;
      if (
        event.target.closest(
          '.tattoo-preview-studio__tools, .tattoo-preview-studio__adjustments, .tattoo-preview-studio__tattoo-overlay, button, input, label, a'
        )
      ) {
        return;
      }

      var pointerId = event.pointerId != null ? event.pointerId : 'touch';
      this.activePointers.set(pointerId, { x: event.clientX, y: event.clientY });

      if (this.activePointers.size >= 2) {
        event.preventDefault();
        this.initGesture();
      }

      if (this.elements.stageShell.setPointerCapture && event.pointerId != null) {
        try {
          this.elements.stageShell.setPointerCapture(event.pointerId);
        } catch (error) {
          // Some browsers release capture during multi-touch transitions.
        }
      }
    }

    onDrag(event) {
      if (this.touchGesture && event.pointerType !== 'mouse') return;
      var pointerId = event.pointerId != null ? event.pointerId : 'mouse';
      if (this.activePointers.has(pointerId)) {
        this.activePointers.set(pointerId, { x: event.clientX, y: event.clientY });
      }

      if (this.gesture && this.activePointers.size >= 2) {
        event.preventDefault();
        this.handleGesture();
        return;
      }

      if (!this.state.isDragging || !this.elements.stageShell) return;
      event.preventDefault();
      var rect = this.elements.stageShell.getBoundingClientRect();
      var x = ((event.clientX + this.dragOffset.x - rect.left) / rect.width) * 100;
      var y = ((event.clientY + this.dragOffset.y - rect.top) / rect.height) * 100;
      this.state.tattoo.x = x;
      this.state.tattoo.y = y;
      this.clampTattooToRegion();
      this.updateTattooOverlay();
    }

    endDrag(event) {
      if (event && event.pointerId != null) {
        this.activePointers.delete(event.pointerId);
      } else {
        this.activePointers.clear();
      }

      if (this.activePointers.size < 2) {
        this.gesture = null;
      }

      if (this.activePointers.size > 0) return;
      this.state.isDragging = false;
      this.elements.overlay?.classList.remove('is-dragging');
      this.elements.stageShell?.classList.remove('is-drag-mode');
    }

    initGesture() {
      var points = this.getGesturePoints();
      if (points.length < 2 || !this.elements.stageShell) return;

      this.state.isDragging = false;
      this.elements.overlay?.classList.add('is-dragging');
      this.elements.stageShell.classList.add('is-drag-mode');

      this.gesture = {
        distance: this.pointerDistance(points[0], points[1]),
        angle: this.pointerAngle(points[0], points[1]),
        center: this.pointerCenter(points[0], points[1]),
        sizeCm: this.state.tattoo.sizeCm,
        rotation: this.state.tattoo.rotation,
        x: this.state.tattoo.x,
        y: this.state.tattoo.y
      };
      this.showGestureHud(1400);
    }

    handleGesture() {
      var points = this.getGesturePoints();
      if (!this.gesture || points.length < 2 || !this.elements.stageShell) return;

      var distance = this.pointerDistance(points[0], points[1]);
      var angle = this.pointerAngle(points[0], points[1]);
      var center = this.pointerCenter(points[0], points[1]);
      var rect = this.elements.stageShell.getBoundingClientRect();

      if (this.settings.enableScale && this.gesture.distance > 0) {
        var ratio = distance / this.gesture.distance;
        this.state.tattoo.sizeCm = clamp(this.gesture.sizeCm * ratio, this.settings.minSizeCm, this.settings.maxSizeCm);
        this.state.tattoo.scale = this.sizeCmToScale(this.state.tattoo.sizeCm);
      }

      if (this.settings.enableRotate) {
        this.state.tattoo.rotation = clamp(this.gesture.rotation + this.angleDelta(angle, this.gesture.angle), -180, 180);
      }

      if (this.settings.enableDrag && rect.width && rect.height) {
        this.state.tattoo.x = this.gesture.x + ((center.x - this.gesture.center.x) / rect.width) * 100;
        this.state.tattoo.y = this.gesture.y + ((center.y - this.gesture.center.y) / rect.height) * 100;
        this.clampTattooToRegion();
      }

      this.updateAdjustmentControls();
      this.updateTattooOverlay();
      this.showGestureHud(900);
      this.maybeVibrateForSize();
    }

    getGesturePoints() {
      return Array.from(this.activePointers.values()).slice(0, 2);
    }

    pointerDistance(a, b) {
      return Math.hypot(b.x - a.x, b.y - a.y);
    }

    pointerAngle(a, b) {
      return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    }

    pointerCenter(a, b) {
      return {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2
      };
    }

    getTouchPoints(event) {
      return Array.from(event.touches || [])
        .slice(0, 2)
        .map((touch) => ({ x: touch.clientX, y: touch.clientY }));
    }

    startTouchGesture(event) {
      var canGesture = this.settings.enableScale || this.settings.enableRotate;
      if (!mobileQuery.matches || !canGesture || !this.elements.stageShell || !this.elements.overlay || this.elements.overlay.hidden) return;
      if (!event.touches || event.touches.length < 2) return;
      if (
        event.target.closest &&
        event.target.closest('.tattoo-preview-studio__tools, .tattoo-preview-studio__adjustments, button, input, label, a')
      ) {
        return;
      }

      var points = this.getTouchPoints(event);
      if (points.length < 2) return;
      event.preventDefault();
      this.activePointers.clear();
      this.gesture = null;
      this.state.isDragging = false;
      this.elements.overlay?.classList.add('is-dragging');
      this.elements.stageShell.classList.add('is-drag-mode');
      this.touchGesture = {
        distance: this.pointerDistance(points[0], points[1]),
        angle: this.pointerAngle(points[0], points[1]),
        center: this.pointerCenter(points[0], points[1]),
        sizeCm: this.state.tattoo.sizeCm,
        rotation: this.state.tattoo.rotation,
        x: this.state.tattoo.x,
        y: this.state.tattoo.y
      };
      this.showGestureHud(1400);
    }

    handleTouchGesture(event) {
      if (!this.touchGesture || !event.touches || event.touches.length < 2 || !this.elements.stageShell) return;
      var points = this.getTouchPoints(event);
      if (points.length < 2) return;
      event.preventDefault();

      var distance = this.pointerDistance(points[0], points[1]);
      var angle = this.pointerAngle(points[0], points[1]);
      var center = this.pointerCenter(points[0], points[1]);
      var rect = this.elements.stageShell.getBoundingClientRect();

      if (this.settings.enableScale && this.touchGesture.distance > 0) {
        var ratio = distance / this.touchGesture.distance;
        this.state.tattoo.sizeCm = clamp(this.touchGesture.sizeCm * ratio, this.settings.minSizeCm, this.settings.maxSizeCm);
        this.state.tattoo.scale = this.sizeCmToScale(this.state.tattoo.sizeCm);
      }

      if (this.settings.enableRotate) {
        this.state.tattoo.rotation = clamp(this.touchGesture.rotation + this.angleDelta(angle, this.touchGesture.angle), -180, 180);
      }

      if (this.settings.enableDrag && rect.width && rect.height) {
        this.state.tattoo.x = this.touchGesture.x + ((center.x - this.touchGesture.center.x) / rect.width) * 100;
        this.state.tattoo.y = this.touchGesture.y + ((center.y - this.touchGesture.center.y) / rect.height) * 100;
        this.clampTattooToRegion();
      }

      this.updateAdjustmentControls();
      this.updateTattooOverlay();
      this.showGestureHud(900);
      this.maybeVibrateForSize();
    }

    endTouchGesture(event) {
      if (!this.touchGesture) return;
      if (event.touches && event.touches.length >= 2) return;
      this.touchGesture = null;
      this.activePointers.clear();
      this.gesture = null;
      this.elements.overlay?.classList.remove('is-dragging');
      this.elements.stageShell?.classList.remove('is-drag-mode');
    }

    angleDelta(current, start) {
      var delta = current - start;
      while (delta > 180) delta -= 360;
      while (delta < -180) delta += 360;
      return delta;
    }

    clampTattooToRegion() {
      var region = this.getAllowedRegion();
      this.state.tattoo.x = clamp(this.state.tattoo.x, region.minX, region.maxX);
      this.state.tattoo.y = clamp(this.state.tattoo.y, region.minY, region.maxY);
    }

    getAllowedRegion() {
      return {
        left: 0,
        top: 0,
        width: 100,
        height: 100,
        minX: 0,
        minY: 0,
        maxX: 100,
        maxY: 100
      };
    }

    updateAllowedRegion() {
      if (!this.elements.allowedRegion) return;
      var region = this.getAllowedRegion();
      this.elements.allowedRegion.style.left = region.left + '%';
      this.elements.allowedRegion.style.top = region.top + '%';
      this.elements.allowedRegion.style.width = region.width + '%';
      this.elements.allowedRegion.style.height = region.height + '%';
    }

    updateProductCards() {
      this.elements.productCards.forEach((card) => {
        var selected = card.dataset.productId === this.state.selectedProductId;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.renderMobileDesignResults();
    }

    updateBodyPartButtons() {
      this.elements.bodyPartButtons.forEach((button) => {
        button.classList.toggle('is-active', normalizeBodyPart(button.dataset.bodyPart) === this.state.activeBodyPart);
      });
      if (this.elements.bodyPartLabel) {
        this.elements.bodyPartLabel.textContent = this.poseLabel(this.state.activeBodyPart);
      }
    }

    updateTabButtons() {
      this.elements.tabButtons.forEach((button) => {
        var tab = button.dataset.studioTab || 'all';
        var group = button.dataset.productGroup || '';
        var active = tab === this.state.activeTab && group === this.state.activeProductGroup;
        button.classList.toggle('is-active', active);
      });
    }

    syncMobileProductRailMode() {
      if (mobileQuery.matches && this.state.activeTab !== 'all' && this.state.activeTab !== 'group') {
        this.state.activeTab = 'all';
        this.state.activeProductGroup = '';
      }
      var activeButton = this.elements.mobileSeriesButtons.find((button) => {
        return (
          (button.dataset.mobileSeriesTab || 'all') === this.state.activeTab &&
          (button.dataset.mobileSeriesGroup || '') === this.state.activeProductGroup
        );
      });
      if (activeButton) {
        this.state.mobileSeriesLabel = safeText(activeButton.dataset.mobileSeriesLabel || activeButton.textContent, this.state.mobileSeriesLabel);
      } else if (!this.state.mobileSeriesLabel && this.elements.mobileProductLabel) {
        this.state.mobileSeriesLabel = this.elements.mobileProductLabel.textContent.trim();
      }
      this.updateTabButtons();
      this.updateMobileSeriesButtons();
      this.updateMobileProductLabel();
    }

    openMobileDesignsPanel() {
      if (!this.elements.mobileDesignsPanel) return;
      this.closeAdjustments();
      this.closePurchaseConfirm();
      this.closeStageFocus();
      this.elements.mobileDesignsPanel.hidden = false;
      this.section.classList.add('is-mobile-designs-open');
      if (this.elements.mobileProductTrigger) {
        this.elements.mobileProductTrigger.setAttribute('aria-expanded', 'true');
      }
      this.renderMobileDesignResults();
    }

    closeMobileDesignsPanel() {
      if (!this.elements.mobileDesignsPanel || this.elements.mobileDesignsPanel.hidden) return;
      this.elements.mobileDesignsPanel.hidden = true;
      this.section.classList.remove('is-mobile-designs-open');
      if (this.elements.mobileProductTrigger) {
        this.elements.mobileProductTrigger.setAttribute('aria-expanded', 'false');
      }
    }

    selectMobileSeries(button) {
      if (!button) return;
      this.state.activeTab = button.dataset.mobileSeriesTab || 'all';
      this.state.activeProductGroup = button.dataset.mobileSeriesGroup || '';
      this.state.mobileSeriesLabel = safeText(button.dataset.mobileSeriesLabel || button.textContent, '');
      this.updateTabButtons();
      this.updateMobileSeriesButtons();
      this.updateMobileProductLabel();
      this.applyFilters();
      this.renderMobileDesignResults();
    }

    updateMobileSearch(value) {
      this.state.mobileSearchQuery = String(value || '').trim();
      this.renderMobileDesignResults();
    }

    renderMobileDesignResults() {
      if (!this.elements.mobileDesignResults) return;
      var cards = this.elements.productCards
        .map((card) => {
          var product = this.productsById.get(String(card.dataset.productId));
          return product && this.productMatchesMobileDesignResult(product, card) ? { product, card } : null;
        })
        .filter(Boolean);

      this.elements.mobileDesignResults.innerHTML = '';

      if (!cards.length) {
        var empty = document.createElement('p');
        empty.className = 'tattoo-preview-studio__mobile-designs-empty';
        empty.textContent = 'No designs found.';
        this.elements.mobileDesignResults.append(empty);
        return;
      }

      cards.forEach(({ product }) => {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'tattoo-preview-studio__mobile-design-result';
        button.dataset.mobileDesignProduct = product.id;
        button.classList.toggle('is-selected', product.id === this.state.selectedProductId);

        var image = document.createElement('img');
        image.alt = product.imageAlt || product.title || '';
        image.loading = 'lazy';
        image.src = product.featuredImage || product.previewImage || TRANSPARENT_PIXEL;

        var meta = document.createElement('span');
        var title = document.createElement('strong');
        title.textContent = product.title || 'Design';
        var price = document.createElement('small');
        price.textContent = product.price || '';
        meta.append(title, price);

        button.append(image, meta);
        this.elements.mobileDesignResults.append(button);
      });
    }

    productMatchesMobileDesignResult(product, card) {
      if (!product) return false;
      var search = String(this.state.mobileSearchQuery || '').trim().toLowerCase();
      if (search) {
        var haystack = [
          product.title,
          product.productType,
          product.groupTitle,
          product.sizeLabel,
          product.price,
          Array.isArray(product.tags) ? product.tags.join(' ') : ''
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(search);
      }

      if (this.state.activeTab === 'group') {
        return card.dataset.productGroup === this.state.activeProductGroup;
      }

      return true;
    }

    async selectMobileDesignResult(event) {
      if (!event.target.closest) return;
      var button = event.target.closest('[data-mobile-design-product]');
      if (!button || !this.elements.mobileDesignResults.contains(button)) return;
      await this.selectProduct(button.dataset.mobileDesignProduct, { respectCurrentPose: true });
      this.closeMobileDesignsPanel();
    }

    updateMobileSeriesButtons() {
      this.elements.mobileSeriesButtons.forEach((button) => {
        var active =
          (button.dataset.mobileSeriesTab || 'all') === this.state.activeTab &&
          (button.dataset.mobileSeriesGroup || '') === this.state.activeProductGroup;
        button.classList.toggle('is-active', active);
      });
    }

    updateMobileProductLabel() {
      if (!this.elements.mobileProductLabel) return;
      var label = this.state.mobileSeriesLabel || this.elements.mobileProductLabel.textContent.trim() || 'All Designs';
      this.elements.mobileProductLabel.textContent = label;
    }

    updateSelectedCard(product) {
      var selectedVariant = this.getSelectedVariant(product);
      if (this.elements.selectedTitle) {
        this.elements.selectedTitle.textContent = product ? product.title : 'Choose a design';
      }
      if (this.elements.selectedDetail) {
        var details = [];
        if (product?.groupTitle) details.push(product.groupTitle);
        if (product && !product.rawUploadImage) details.push(this.formatVariantTitle(selectedVariant, product));
        if (selectedVariant?.price || product?.price) details.push(selectedVariant?.price || product.price);
        this.elements.selectedDetail.textContent = product ? details.join(' / ') || 'Available for preview' : 'Preview details appear after selection.';
      }
      if (this.elements.selectedImage) {
        var source = product ? product.featuredImage || product.previewImage : '';
        this.elements.selectedImage.src = source || TRANSPARENT_PIXEL;
        this.elements.selectedImage.alt = product ? product.title : '';
      }
      if (this.elements.selectedLink) {
        if (product?.url) {
          this.elements.selectedLink.href = product.url;
          this.elements.selectedLink.hidden = false;
        } else {
          this.elements.selectedLink.href = '#';
          this.elements.selectedLink.hidden = true;
        }
      }
      this.updateSizeReference();
    }

    updateAddButton() {
      var product = this.getSelectedProduct();
      var selectedVariant = this.getSelectedVariant(product);
      var canAdd = Boolean(product && product.available && selectedVariant && selectedVariant.available && selectedVariant.id);
      var hasAvailableProduct = this.products.some((candidate) => {
        var variant = this.getSelectedVariant(candidate);
        return Boolean(candidate && candidate.available && variant && variant.available && variant.id);
      });
      var previewOnly = Boolean(product && product.rawUploadImage) || (!canAdd && !hasAvailableProduct);
      if (this.elements.addButton) {
        this.elements.addButton.disabled = !canAdd;
      }
      if (this.elements.addPrice) {
        this.elements.addPrice.textContent = previewOnly ? '' : selectedVariant && selectedVariant.price ? selectedVariant.price : product && product.price ? product.price : '';
      }
      if (this.elements.addLabel) {
        this.elements.addLabel.textContent = previewOnly ? 'Preview Only' : canAdd ? (mobileQuery.matches ? 'Add' : 'Add to Cart') : 'Unavailable';
      }
      if (!canAdd) {
        this.closePurchaseConfirm();
      } else if (this.elements.purchaseConfirm && !this.elements.purchaseConfirm.hidden) {
        this.updatePurchaseConfirm();
      }
    }

    openPurchaseConfirm() {
      var product = this.getSelectedProduct();
      var selectedVariant = this.getSelectedVariant(product);
      if (!product || !product.available || !selectedVariant || !selectedVariant.available || !selectedVariant.id) {
        this.setStatus('Select an available design first.');
        return;
      }
      if (!this.elements.purchaseConfirm) return;
      this.closeAdjustments();
      this.closeMobileDesignsPanel();
      this.closeStageFocus();
      this.updatePurchaseConfirm();
      this.elements.purchaseConfirm.hidden = false;
      this.section.classList.add('is-confirming-purchase');
      window.setTimeout(() => {
        this.elements.confirmAddButton?.focus({ preventScroll: true });
      }, 0);
    }

    closePurchaseConfirm() {
      if (!this.elements.purchaseConfirm || this.elements.purchaseConfirm.hidden) return;
      this.elements.purchaseConfirm.hidden = true;
      this.section.classList.remove('is-confirming-purchase');
    }

    updatePurchaseConfirm() {
      var product = this.getSelectedProduct();
      var selectedVariant = this.getSelectedVariant(product);
      if (this.elements.confirmProduct) {
        this.elements.confirmProduct.textContent = product ? product.title : 'Choose a design';
      }
      this.renderVariantOptions(product);
      if (this.elements.confirmBody) {
        this.elements.confirmBody.textContent = this.poseLabel(this.state.activeBodyPart);
      }
      if (this.elements.confirmSize) {
        this.elements.confirmSize.textContent = this.formatCurrentSize().full;
      }
      if (this.elements.confirmRotate) {
        this.elements.confirmRotate.textContent = Math.round(this.state.tattoo.rotation) + ' deg';
      }
      if (this.elements.confirmAddButton) {
        this.elements.confirmAddButton.textContent =
          selectedVariant && selectedVariant.price ? 'Confirm Add to Cart - ' + selectedVariant.price : 'Confirm Add to Cart';
      }
    }

    updateHint() {
      var product = this.getSelectedProduct();
      if (!this.elements.placementHint || !product) return;
      var recommended = product.normalizedBodyPart;
      if (recommended && recommended !== this.state.activeBodyPart) {
        this.elements.placementHint.textContent =
          'This design is recommended for ' + this.poseLabel(recommended) + ', but you can preview it here.';
      } else {
        this.elements.placementHint.textContent = '';
      }
    }

    poseLabel(bodyPart) {
      var normalized = normalizeBodyPart(bodyPart);
      var pose = this.posesByPart.get(normalized);
      return pose && pose.label ? pose.label : labelFromBodyPart(normalized);
    }

    applyFilters() {
      var visibleCount = 0;

      this.elements.productCards.forEach((card) => {
        var product = this.productsById.get(String(card.dataset.productId));
        var visible = this.productMatchesFilters(product, card);
        var tile = card.closest('.tattoo-preview-studio__product-tile');
        card.classList.toggle('is-hidden', !visible);
        card.hidden = !visible;
        if (tile) tile.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.elements.filterEmpty) {
        this.elements.filterEmpty.hidden = visibleCount !== 0;
      }
    }

    productMatchesFilters(product, card) {
      if (!product) return false;
      if (this.filter.category && product.productType !== this.filter.category) return false;
      if (this.filter.bodyPart && product.normalizedBodyPart !== this.filter.bodyPart) return false;

      if (this.state.activeTab === 'best') {
        if (!product.tagsNormalized.includes(this.bestSellerTag)) return false;
      } else if (this.state.activeTab === 'new') {
        if (!product.tagsNormalized.includes(this.newInTag)) return false;
      } else if (this.state.activeTab === 'group') {
        if (card.dataset.productGroup !== this.state.activeProductGroup) return false;
      }

      return true;
    }

    ensureVisibleSelection() {
      var selectedVisible = this.elements.productCards.some((card) => {
        return card.dataset.productId === this.state.selectedProductId && !card.hidden;
      });

      if (selectedVisible) return;
      var firstVisible = this.elements.productCards.find((card) => !card.hidden);
      if (firstVisible) {
        this.selectProduct(firstVisible.dataset.productId, { respectCurrentPose: true });
      }
    }

    toggleDragMode() {
      this.state.dragMode = !this.state.dragMode;
      if (this.elements.dragButton) {
        this.elements.dragButton.setAttribute('aria-pressed', this.state.dragMode ? 'true' : 'false');
      }
      if (this.elements.stageShell) {
        this.elements.stageShell.classList.toggle('is-drag-mode', this.state.dragMode);
      }
    }

    handleStageShellClick(event) {
      if (this.section.classList.contains('is-stage-focus')) return;
      if (!this.elements.stageShell || !this.elements.stageShell.classList.contains('has-model')) return;
      if (this.elements.adjustmentDetails && this.elements.adjustmentDetails.open) {
        this.closeAdjustments();
        return;
      }
      if (
        event.target.closest(
          '.tattoo-preview-studio__tools, .tattoo-preview-studio__adjustments, .tattoo-preview-studio__tattoo-overlay, button, input, label, a'
        )
      ) {
        return;
      }
      this.openStageFocus();
    }

    openStageFocus() {
      if (!this.elements.stageShell || this.stageFocus) return;
      this.closeAdjustments();
      this.closeMobileDesignsPanel();
      this.closePurchaseConfirm();
      var parent = this.elements.stageShell.parentNode;
      if (!parent) return;
      var placeholder = document.createComment('tattoo-preview-stage-focus-placeholder');
      var overlay = document.createElement('div');
      var viewport = document.createElement('div');
      overlay.className = 'tattoo-preview-studio__stage-focus-overlay';
      overlay.setAttribute('data-stage-focus-overlay', '');
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.style.setProperty('--tps-theme-header-offset', this.section.style.getPropertyValue('--tps-theme-header-offset') || '0px');
      viewport.className = 'tattoo-preview-studio__stage-focus-viewport';
      parent.insertBefore(placeholder, this.elements.stageShell);
      viewport.append(this.elements.stageShell);
      overlay.append(viewport);
      document.body.append(overlay);
      this.stageFocus = { overlay, placeholder, parent };
      this.on(overlay, 'click', (event) => {
        if (event.target === overlay || event.target === viewport) {
          this.closeStageFocus();
        }
      });
      this.section.classList.add('is-stage-focus');
      document.documentElement.classList.add('tattoo-preview-studio--stage-lock');
      this.elements.closeStageFocusButton?.focus({ preventScroll: true });
    }

    closeStageFocus() {
      if (this.stageFocus) {
        var { overlay, placeholder, parent } = this.stageFocus;
        if (parent && placeholder && placeholder.parentNode === parent) {
          parent.insertBefore(this.elements.stageShell, placeholder);
          placeholder.remove();
        }
        overlay?.remove();
        this.stageFocus = null;
      }
      this.section.classList.remove('is-stage-focus');
      document.documentElement.classList.remove('tattoo-preview-studio--stage-lock');
    }

    startToolDrag(event, button) {
      if (!button || !this.elements.stageShell) return;
      event.preventDefault();
      event.stopPropagation();
      var rect = this.elements.stageShell.getBoundingClientRect();
      var startX = toCssNumber(this.section.style.getPropertyValue('--tps-tool-x'), rect.width - 58);
      var startY = toCssNumber(this.section.style.getPropertyValue('--tps-tool-y'), rect.height * 0.62);
      this.toolDrag = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startX,
        startY,
        moved: false
      };
      this.section.classList.add('is-moving-size-tool');
      if (button.setPointerCapture) {
        try {
          button.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture can fail if the browser has already released it.
        }
      }
    }

    onToolDrag(event) {
      if (!this.toolDrag || event.pointerId !== this.toolDrag.pointerId || !this.elements.stageShell) return;
      event.preventDefault();
      var deltaX = event.clientX - this.toolDrag.startClientX;
      var deltaY = event.clientY - this.toolDrag.startClientY;
      if (Math.abs(deltaX) + Math.abs(deltaY) > 14) {
        this.toolDrag.moved = true;
      }
      this.updateToolControlPosition(this.toolDrag.startX + deltaX, this.toolDrag.startY + deltaY, false);
    }

    endToolDrag(event) {
      if (!this.toolDrag || event.pointerId !== this.toolDrag.pointerId) return;
      var shouldOpen = !this.toolDrag.moved;
      this.suppressToolClick = this.toolDrag.moved;
      this.toolDrag = null;
      this.section.classList.remove('is-moving-size-tool');
      if (shouldOpen) {
        this.suppressToolClick = true;
        this.openAdjustments(true);
      }
      if (this.suppressToolClick) {
        window.setTimeout(() => {
          this.suppressToolClick = false;
        }, 180);
      }
    }

    updateToolControlPosition(x, y, expanded) {
      if (!this.elements.stageShell) return;
      var rect = this.elements.stageShell.getBoundingClientRect();
      var marginX = expanded ? 32 : 30;
      var marginY = expanded ? 94 : 32;
      var nextX = clamp(x, marginX, Math.max(marginX, rect.width - marginX));
      var nextY = clamp(y, marginY, Math.max(marginY, rect.height - marginY));
      this.section.style.setProperty('--tps-tool-x', formatNumber(nextX) + 'px');
      this.section.style.setProperty('--tps-tool-y', formatNumber(nextY) + 'px');
    }

    keepToolControlInBounds(expanded) {
      if (!this.elements.stageShell) return;
      var rect = this.elements.stageShell.getBoundingClientRect();
      var currentX = toCssNumber(this.section.style.getPropertyValue('--tps-tool-x'), rect.width - 58);
      var currentY = toCssNumber(this.section.style.getPropertyValue('--tps-tool-y'), rect.height * 0.62);
      this.updateToolControlPosition(currentX, currentY, expanded);
    }

    openAdjustments(forceOpen) {
      if (this.elements.adjustmentDetails) {
        var willOpen = forceOpen === true ? true : !this.elements.adjustmentDetails.open;
        this.elements.adjustmentDetails.open = willOpen;
        this.keepToolControlInBounds(willOpen);
        this.syncAdjustmentButtons();
        if (this.elements.adjustmentDetails.open) {
          this.showGestureHud(1400);
        }
      }
    }

    setInitialAdjustmentState() {
      if (!this.elements.adjustmentDetails) return;
      this.elements.adjustmentDetails.open = false;
      this.syncAdjustmentButtons();
    }

    closeAdjustments() {
      if (!this.elements.adjustmentDetails || !this.elements.adjustmentDetails.open) return;
      this.elements.adjustmentDetails.open = false;
      this.syncAdjustmentButtons();
    }

    syncAdjustmentButtons() {
      var isOpen = Boolean(this.elements.adjustmentDetails && this.elements.adjustmentDetails.open);
      this.section.classList.toggle('is-adjusting-design', isOpen);
      this.elements.openAdjustmentsButtons.forEach((button) => {
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    fitToView() {
      this.section.classList.remove('is-focus-mode');
      this.elements.stage?.scrollIntoView({ behavior: reduceMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
    }

    toggleFocusMode() {
      this.section.classList.toggle('is-focus-mode');
      this.elements.stage?.scrollIntoView({ behavior: reduceMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
    }

    async addToCart() {
      var product = this.getSelectedProduct();
      var selectedVariant = this.getSelectedVariant(product);
      if (!product || !product.available || !selectedVariant || !selectedVariant.available || !selectedVariant.id) {
        this.setStatus('Select an available design first.');
        return;
      }

      this.elements.addButton?.classList.add('is-processing');
      if (this.elements.addButton) this.elements.addButton.disabled = true;
      if (this.elements.addLabel) this.elements.addLabel.textContent = 'Adding...';
      if (this.elements.confirmAddButton) {
        this.elements.confirmAddButton.disabled = true;
        this.elements.confirmAddButton.textContent = 'Adding...';
      }
      this.setStatus('');

      var body = new FormData();
      body.append('id', selectedVariant.id);
      body.append('quantity', '1');
      body.append('sections', 'side-cart');
      body.append('properties[Order Size]', this.formatVariantTitle(selectedVariant, product));
      body.append('properties[Preview Body Part]', this.state.activeBodyPart || '');
      body.append('properties[Preview X]', this.roundValue(this.state.tattoo.x));
      body.append('properties[Preview Y]', this.roundValue(this.state.tattoo.y));
      body.append('properties[Preview Scale]', this.roundValue(this.state.tattoo.scale));
      body.append('properties[Preview Size CM]', formatNumber(this.state.tattoo.sizeCm));
      body.append('properties[Preview Size IN]', formatNumber(cmToInches(this.state.tattoo.sizeCm)));
      body.append('properties[Preview Rotation]', this.roundValue(this.state.tattoo.rotation));
      body.append('properties[Preview Pose]', this.state.activePose ? this.state.activePose.label || this.state.activeBodyPart : '');
      body.append('properties[_Preview Image Used]', product.preparedPreviewImage || product.previewImage || product.featuredImage || '');

      try {
        var response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          body: body,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript'
          }
        });
        var json = await response.json();

        if (!response.ok || json.status) {
          throw new Error(this.extractCartError(json));
        }

        this.setStatus('Added to cart.');
        this.closePurchaseConfirm();
        this.openCart(json);
      } catch (error) {
        this.setStatus(error.message || 'Could not add this design to cart.');
      } finally {
        this.elements.addButton?.classList.remove('is-processing');
        if (this.elements.confirmAddButton) {
          this.elements.confirmAddButton.disabled = false;
        }
        this.updateAddButton();
      }
    }

    extractCartError(response) {
      if (!response) return 'Could not add this design to cart.';
      if (typeof response.description === 'string') return response.description;
      if (response.message) return response.message;
      if (response.description && typeof response.description === 'object') {
        return Object.values(response.description).flat().join(' ');
      }
      return 'Could not add this design to cart.';
    }

    openCart(cartResponse) {
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        if (cartResponse && cartResponse.sections && cartResponse.sections['side-cart']) {
          window.ajaxCart.load(cartResponse, true);
        } else {
          window.ajaxCart.load(false, true, false, true);
        }
        return;
      }

      this.refreshCartCount();
      var cartTrigger = document.querySelector('a[data-panel="cart"]');
      if (cartTrigger) {
        cartTrigger.click();
      } else if (typeof window.openPanel === 'function' && document.getElementById('cart')) {
        window.openPanel('cart');
      }
    }

    refreshCartCount() {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          var count = document.getElementById('cart-count');
          if (count) count.textContent = cart.item_count;
        })
        .catch(() => {});
    }

    async closeStudio() {
      if (this.state.isClosed) return;
      this.state.isClosed = true;

      if (!this.settings.enableCloseAnimation || reduceMotionQuery.matches) {
        this.section.classList.add('tattoo-preview-studio--closed');
        return;
      }

      this.section.classList.add('tattoo-preview-studio--closing');
      await this.waitForTransition(this.section, 620);
      this.section.classList.remove('tattoo-preview-studio--closing');
      this.section.classList.add('tattoo-preview-studio--closed');
    }

    reopenStudio() {
      this.state.isClosed = false;
      this.section.classList.remove('tattoo-preview-studio--closed', 'tattoo-preview-studio--closing');
      if (!reduceMotionQuery.matches) {
        this.section.classList.remove('tattoo-preview-studio--intro');
        window.requestAnimationFrame(() => this.section.classList.add('tattoo-preview-studio--intro'));
      }
      this.elements.stage?.scrollIntoView({ behavior: reduceMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
    }

    preloadImage(source) {
      return new Promise((resolve, reject) => {
        if (!source) {
          resolve();
          return;
        }
        var image = new Image();
        image.onload = resolve;
        image.onerror = reject;
        image.src = source;
      });
    }

    wait(duration) {
      return new Promise((resolve) => window.setTimeout(resolve, duration));
    }

    nextFrame() {
      return new Promise((resolve) => window.requestAnimationFrame(resolve));
    }

    waitForTransition(element, maxDuration) {
      return new Promise((resolve) => {
        var done = false;
        var timer = window.setTimeout(finish, maxDuration);
        function finish() {
          if (done) return;
          done = true;
          window.clearTimeout(timer);
          element.removeEventListener('transitionend', finish);
          resolve();
        }
        element.addEventListener('transitionend', finish, { once: true });
      });
    }

    roundValue(value) {
      return String(Math.round(value * 100) / 100);
    }

    setStatus(message) {
      if (this.elements.status) {
        this.elements.status.textContent = message || '';
      }
    }

    destroy() {
      this.abort.abort();
      instances.delete(this.section);
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var sections = [];
    if (root.matches && root.matches(ROOT_SELECTOR)) {
      sections.push(root);
    }
    if (root.querySelectorAll) {
      sections = sections.concat(Array.from(root.querySelectorAll(ROOT_SELECTOR)));
    }

    sections.forEach((section) => {
      if (instances.has(section)) return;
      var instance = new TattooPreviewStudio(section);
      instances.set(section, instance);
      instance.init();
    });
  }

  function destroyIn(scope) {
    var root = scope || document;
    var sections = [];
    if (root.matches && root.matches(ROOT_SELECTOR)) {
      sections.push(root);
    }
    if (root.querySelectorAll) {
      sections = sections.concat(Array.from(root.querySelectorAll(ROOT_SELECTOR)));
    }

    sections.forEach((section) => {
      var instance = instances.get(section);
      if (instance) instance.destroy();
    });
  }

  function findInstanceBySectionId(sectionId) {
    var section = document.getElementById('shopify-section-' + sectionId);
    if (!section) return null;
    var root = section.matches(ROOT_SELECTOR) ? section : section.querySelector(ROOT_SELECTOR);
    return root ? instances.get(root) : null;
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyIn(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    if (!event.detail || !event.detail.sectionId || !event.detail.blockId) return;
    var instance = findInstanceBySectionId(event.detail.sectionId);
    if (!instance) return;
    var bodyButton = instance.section.querySelector('[data-block-id="' + event.detail.blockId + '"][data-body-part]');
    if (bodyButton) {
      instance.selectBodyPart(bodyButton.dataset.bodyPart, { keepProduct: true });
      return;
    }
    var groupButton = instance.section.querySelector('[data-product-group="' + event.detail.blockId + '"][data-studio-tab="group"]');
    if (groupButton) {
      groupButton.click();
    }
  });

  window.TattooPreviewStudioController = {
    initAll: initAll,
    destroyIn: destroyIn,
    instances: instances
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    }, { once: true });
  } else {
    initAll(document);
  }
})();
